/**
 * Multi-model main program
 * Support loading and managing multiple Fragments models simultaneously
 */

import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import * as THREE from "three";
import * as FRAGS from "@thatopen/fragments";
import { MultiModelManager, ModelConfig } from "./multiModelManager";
import { i18n, Language } from "./i18n";
import { ModelColorController } from "./modelColorController_material";
import { StatusAPI } from "./statusAPI";
import { initCrossOriginSync } from "./crossOriginSync";
import { setupFakeCamerasForAllModels } from "./fakeCameraSetup";
import { AuthClient } from "./authClient";

/**
 * Hand off a list of models to the viewer page WITHOUT putting any of
 * the (huge, signed) R2 URLs in the query string. Stash the payload in
 * localStorage under a one-shot key and pass only that key + folder
 * name in the URL. The viewer consumes and deletes the key, then
 * re-signs every fileId itself via /api/files/:id/url.
 *
 * Why localStorage and not sessionStorage:
 *   `window.open(..., '_blank')` opens a fresh tab with its own
 *   sessionStorage scope, so a sessionStorage write on the opener tab
 *   is NOT visible to the newly opened viewer. localStorage IS shared
 *   across same-origin tabs, so the viewer can read it back.
 *
 * Why a UUID key:
 *   Avoids race conditions when the user clicks "Open Viewer" on
 *   multiple folders in quick succession; each tab owns its own key
 *   and consumes it independently.
 *
 * Backwards-compat fallback: if for any reason localStorage is
 * unavailable (private browsing quota, etc.) AND the payload is small
 * enough to safely fit in a URL (~6 KB stringified, ~20-ish models),
 * fall back to the legacy ?models= query param. Larger payloads in
 * that path will throw rather than silently break Safari.
 */
function openViewerWithPayload(
  models: Array<{ id: string; name: string; fileId?: string; modelType?: string; url?: string }>,
  folderName: string
): string {
  const STORAGE_PREFIX = "echov-viewer-payload-";
  const key =
    "p-" +
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2, 10);
  const json = JSON.stringify({ folder: folderName, models, createdAt: Date.now() });

  try {
    localStorage.setItem(STORAGE_PREFIX + key, json);
    return `viewer/viewer.html?session=${encodeURIComponent(key)}&folder=${encodeURIComponent(folderName)}`;
  } catch (err) {
    console.warn("[openViewerWithPayload] localStorage write failed, falling back to URL params:", err);
    const encoded = encodeURIComponent(JSON.stringify(models));
    if (encoded.length > 6000) {
      throw new Error(
        `Cannot open viewer: payload too large (${encoded.length} chars) ` +
          `and localStorage is unavailable. Try a smaller folder or disable private browsing.`
      );
    }
    return `viewer/viewer.html?models=${encoded}&folder=${encodeURIComponent(folderName)}`;
  }
}

// Inject custom Popup styles - must execute after Mapbox CSS loads
const markerPopupStyle = document.createElement('style');
markerPopupStyle.textContent = `
  .mapboxgl-popup.marker-popup-dark {
    z-index: 99999 !important;
  }
  
  .mapboxgl-popup.marker-popup-dark .mapboxgl-popup-content {
    background: rgba(0, 0, 0, 0.95) !important;
    background-color: rgba(0, 0, 0, 0.95) !important;
    backdrop-filter: blur(10px) !important;
    border: 1px solid #44d3de !important;
    border-radius: 4px !important;
    padding: 0 !important;
    box-shadow: 
      0 0 10px rgba(68, 211, 222, 0.6),
      0 0 20px rgba(68, 211, 222, 0.3) !important;
    outline: none !important;
    pointer-events: auto !important;
    color: #44d3de !important;
  }
  
  .mapboxgl-popup.marker-popup-dark .mapboxgl-popup-content > div {
    background: rgba(0, 0, 0, 0.95) !important;
  }
  
  .mapboxgl-popup.marker-popup-dark .mapboxgl-popup-close-button {
    color: #44d3de !important;
    font-size: 16px !important;
    padding: 2px 6px !important;
    right: 2px !important;
    top: 2px !important;
    background: transparent !important;
    background-color: transparent !important;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    z-index: 100000 !important;
  }
  
  .mapboxgl-popup.marker-popup-dark .mapboxgl-popup-close-button:hover {
    background: rgba(68, 211, 222, 0.2) !important;
    background-color: rgba(68, 211, 222, 0.2) !important;
    color: #fff !important;
  }
  
  .mapboxgl-popup.marker-popup-dark .mapboxgl-popup-tip {
    border-top-color: rgba(0, 0, 0, 0.95) !important;
    border-bottom-color: rgba(0, 0, 0, 0.95) !important;
  }
`;
document.head.appendChild(markerPopupStyle);

// ========================================
// Functional Marker Interface
// ========================================

interface VideoMarker {
  _id?: string;
  name: string;
  folderId: string;
  folderName?: string;
  color: string;
  position: {
    lng: number;
    lat: number;
    altitude?: number;
  };
  markerType?: 'video' | 'device' | 'information' | 'location';
  label?: string;
  videoUrl?: string;
  videoType?: string;
  deviceData?: Array<{key: string, value: string}>;
  infoText?: string;
}

let videoMarkers: VideoMarker[] = [];
const markerElements = new Map<string, mapboxgl.Marker>();

// Marker-specific Three.js scene
let markerScene: THREE.Scene | null = null;
let markerCamera: THREE.Camera | null = null;
let markerRenderer: THREE.WebGLRenderer | null = null;
const markerMeshes = new Map<string, THREE.Group>();

// Marker DOM elements for click detection
const markerDOMElements = new Map<string, HTMLDivElement>();
let currentMarkerPopup: mapboxgl.Popup | null = null;

/**
 * 每個 marker 自己的 popup 註冊表 — 防止同一個 marker 連按多下就疊出
 * 多份 popup。Key 規則見 showMarkerPopup() 開頭註解。
 */
const activeMarkerPopups = new Map<string, mapboxgl.Popup>();

/**
 * popup zoom-aware scaling
 *
 * Mapbox v3 的 popup 是純 DOM，CSS 都是固定 px → zoom in/out 時 popup
 * 永遠是同一個螢幕大小，看起來就像「貼」在 viewport 上、跟 3D 地圖完全
 * 脫節（zoom out 時 popup 比整條街都大、zoom in 時 popup 又比 marker
 * icon 細好多）。
 *
 * 用家期望：popup 應該像個有真實世界尺寸的物件 —— zoom in 變大、zoom
 * out 變細，跟 marker icon、3D model 一齊呼吸。
 *
 * 做法：
 *   1. popup 開啟時記下當下 zoom 做 reference (= scale 1)
 *   2. 每 frame 在 map 'render' 計 scale = 2 ^ (currentZoom - refZoom)
 *      (mercator 投影下 pixelsPerMeter 隨 zoom 以 2 為底指數變化)
 *   3. 套到 .mapboxgl-popup-content 上，transform-origin: bottom
 *      center，令 popup 由底部 (= tip / anchor 點) 往上放大縮細，視覺
 *      上仍然「黐住」marker
 *   4. tip 用 origin: top center scale，保持 tip 頂連住 content 底
 *   5. cap [0.1, 10] 防極端 zoom 把 popup 縮成肉眼睇唔到、或脹爆 viewport
 *
 * Listener 全 map 一個就夠，迭代 activeMarkerPopups 即可；popup close
 * 時自動從 activeMarkerPopups 移除、下次 render 就略過。
 */
let popupScaleWired = false;
function applyPopupScale(popup: mapboxgl.Popup, currentZoom: number): void {
  const refZoom = (popup as any)._scaleReferenceZoom;
  if (typeof refZoom !== 'number') return;

  const rawScale = Math.pow(2, currentZoom - refZoom);
  // Cap [0.1, 10] —— 還原 commit cd6cdec 原裝設定（後來嘅 [0.6, 1.5] /
  // [0, 1.5] 試圖兼顧 video iframe 但破壞咗「popup 當 3D 物件」嘅效果）：
  //   - 下限 0.1：zoom out 多級可以縮到原 size 10%，仍然睇到、唔會
  //     完全消失，但又夠細令 popup 真係似遠方物件
  //   - 上限 10：zoom in 可以放大到 10 倍，跟住 marker icon / 模型一齊
  //     呼吸；video iframe 嘅 layout box（min(360px,70vw)）本身已 cap
  //     喺 viewport 70%，scale 唔改 layout，所以 content 喺 scale 1 已
  //     fit 入 viewport，視覺超出嗰部份係 user 主動 zoom in 嘅後果，
  //     可接受
  const scale = Math.max(0.1, Math.min(10, rawScale));

  const el = popup.getElement();
  if (!el) return;
  const content = el.querySelector('.mapboxgl-popup-content') as HTMLElement | null;
  const tip = el.querySelector('.mapboxgl-popup-tip') as HTMLElement | null;
  if (content) {
    content.style.transformOrigin = 'bottom center';
    content.style.transform = `scale(${scale})`;
  }
  if (tip) {
    tip.style.transformOrigin = 'top center';
    tip.style.transform = `scale(${scale})`;
  }
}
function ensurePopupScaleWired(): void {
  if (popupScaleWired || !map) return;
  popupScaleWired = true;
  map.on('render', () => {
    if (activeMarkerPopups.size === 0) return;
    const z = map.getZoom();
    for (const popup of activeMarkerPopups.values()) {
      applyPopupScale(popup, z);
    }
  });
}

/**
 * Attach 一個 streaming video player 到 wrapper element 內。
 * 根據 type 揀 backend：
 *   - 'mp4'           : HTML5 native <video src=>
 *   - 'hls' / 'm3u8'  : Safari 行 native；其他用 hls.js (lazy import)
 *   - 'flv'           : flv.js (lazy import)
 *   - 其他 / 未知     : fallback 返 iframe (legacy embed URL)
 *
 * Lazy import 嘅原因 —— hls.js + flv.js 加埋 ~150KB gzip。app 啟動時
 * 大部份 user 都唔會即刻按 video marker，唔值得入主 bundle，要按到
 * 第一次先 load 個 chunk。Vite 識自動 split。
 *
 * 回傳 cleanup function：popup close 時 call，destroy player + stop
 * segment loader。冇 cleanup 的話 live stream 會永遠喺 background 跑、
 * 食 bandwidth + leak memory。
 */
function attachVideoPlayer(
  wrapper: HTMLElement,
  url: string,
  type: string
): () => void {
  // ── shared fallback：用 iframe (僅後備、適合 youtube / vimeo embed)
  const useIframeFallback = (reason?: string) => {
    if (reason) console.warn('[marker-video] iframe fallback:', reason);
    wrapper.innerHTML = '';
    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      border: 0;
    `;
    iframe.setAttribute('allowfullscreen', 'true');
    wrapper.appendChild(iframe);
    return () => {
      // iframe 唔需要主動 destroy，DOM removal 已經足夠 stop 載入
      try { iframe.src = 'about:blank'; } catch { /* noop */ }
    };
  };

  const t = (type || '').toLowerCase().replace(/^\.+/, '');

  // 已知 stream type → 用 <video>
  if (t === 'hls' || t === 'm3u8' || t === 'flv' || t === 'mp4') {
    const video = document.createElement('video');
    video.controls = true;
    video.autoplay = true;
    // Most browsers block unmuted autoplay until user-gesture; muted by
    // default 等 stream 起得身嚟，user 自己可以 unmute。
    video.muted = true;
    video.setAttribute('playsinline', 'true');
    video.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      background: #000;
      object-fit: contain;
    `;
    wrapper.appendChild(video);

    // disposed flag —— 處理 race condition：popup close 早過 hls.js /
    // flv.js dynamic import resolve。冇呢個 flag 嘅話 import 完成後仲會
    // new Hls() 然後喺 background 食 bandwidth、orphan 一條 stream。
    let disposed = false;

    // baseline cleanup — pause + 解除 src 等 GC 收返晒 buffer
    let cleanup: () => void = () => {
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch { /* noop */ }
    };

    if (t === 'mp4') {
      video.src = url;
      video.play().catch(() => { /* autoplay blocked - 用 controls 手動播 */ });
      return () => { disposed = true; cleanup(); };
    }

    if (t === 'hls' || t === 'm3u8') {
      // Safari + iOS 內建 HLS：直接 src= 就播得，唔需要 hls.js
      const nativeHls = video.canPlayType('application/vnd.apple.mpegurl');
      if (nativeHls) {
        video.src = url;
        video.play().catch(() => {});
        return () => { disposed = true; cleanup(); };
      }
      // 其他 browser：lazy import hls.js
      import('hls.js').then(mod => {
        if (disposed) return;
        const Hls = (mod as any).default || mod;
        if (!Hls || !Hls.isSupported || !Hls.isSupported()) {
          useIframeFallback('hls.js not supported');
          return;
        }
        const hls = new Hls({
          // live stream 友善設定：低 latency + 喺斷線時自動重 connect
          lowLatencyMode: true,
          maxBufferLength: 30,
        });
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (disposed) return;
          video.play().catch(() => {});
        });
        hls.on(Hls.Events.ERROR, (_evt: any, data: any) => {
          if (data?.fatal) {
            console.warn('[marker-video] HLS fatal error', data);
          }
        });
        const prev = cleanup;
        cleanup = () => {
          try { hls.destroy(); } catch { /* noop */ }
          prev();
        };
      }).catch(err => {
        if (disposed) return;
        useIframeFallback(`hls.js import failed: ${err}`);
      });
      return () => { disposed = true; cleanup(); };
    }

    if (t === 'flv') {
      // flv.js 需要 MSE，移動 Safari 同 iOS 全部唔 support → 直接 fallback
      // 唔 hard-fail。lazy import 之後檢 isSupported。
      import('flv.js').then(mod => {
        if (disposed) return;
        const flvjs = (mod as any).default || mod;
        if (!flvjs || !flvjs.isSupported || !flvjs.isSupported()) {
          useIframeFallback('flv.js not supported on this browser');
          return;
        }
        const player = flvjs.createPlayer({
          type: 'flv',
          url,
          isLive: true,
          hasAudio: true,
          hasVideo: true,
        });
        player.attachMediaElement(video);
        player.load();
        try { player.play(); } catch { /* noop */ }
        const prev = cleanup;
        cleanup = () => {
          try {
            player.pause();
            player.unload();
            player.detachMediaElement();
            player.destroy();
          } catch { /* noop */ }
          prev();
        };
      }).catch(err => {
        if (disposed) return;
        useIframeFallback(`flv.js import failed: ${err}`);
      });
      return () => { disposed = true; cleanup(); };
    }
  }

  // 未知 type → 全部交畀 iframe
  return useIframeFallback();
}

let markersVisible: boolean = true;
let markerZoomEnabled: boolean = true;  // Control whether marker click zooms in first

// 為 Mapbox 創建 marker icon images (使用 Google Material Symbols PNG)
// 預載的 icon 圖片緩存
const iconImageCache = new Map<string, HTMLImageElement>();

/**
 * 把 hex 色 (#rrggbb / #rgb，可選帶或不帶 #) 轉成 rgba(r,g,b,a) 字串，
 * 用於需要套 alpha 的場景（marker neon glow 等）。
 * 無效輸入回 null，呼叫端 fallback 用原色。
 */
function hexToRgba(hex: string, alpha: number): string | null {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.trim();
  if (h.startsWith('#')) h = h.slice(1);
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

// 創建 marker DOM 元素
async function createMarkerElement(marker: VideoMarker): Promise<HTMLDivElement> {
  const el = document.createElement('div');
  const size = 21; // 縮小 50%: 42 * 0.5 = 21
  
  // 創建 canvas 來繪製 marker icon
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  
  // 繪製圓形背景 (使用 marker 的顏色)
  ctx.fillStyle = marker.color;
  ctx.beginPath();
  ctx.arc(size/2, size/2, size/2 - 2, 0, Math.PI * 2);
  ctx.fill();
  
  // 繪製白色邊框
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(size/2, size/2, size/2 - 2, 0, Math.PI * 2);
  ctx.stroke();
  
  // 繪製白色的 icon (從緩存中獲取)
  const iconImg = iconImageCache.get(marker.markerType || 'video');
  if (iconImg) {
    const iconSize = size * 0.6;
    const iconX = (size - iconSize) / 2;
    const iconY = (size - iconSize) / 2;
    ctx.drawImage(iconImg, iconX, iconY, iconSize, iconSize);
  }
  
  // 設置元素樣式
  el.className = 'marker functional-marker-icon';
  el.style.backgroundImage = `url(${canvas.toDataURL()})`;
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.backgroundSize = '100%';
  el.style.cursor = 'pointer';
  el.style.borderRadius = '50%';
  
  // Neon light 效果
  //
  // 之前 4 層 full-alpha glow (10/20/30/40px) 太誇張 — 多個 marker 集中
  // 一個區域時 viewport 變成一片發光色塊，搵唔到 icon 本體。改用 2 層
  // 0.7 alpha 的內外圈，視覺上仍係 neon 但唔再洗版。
  // hexToRgba 把 marker.color (#rrggbb 或 #rgb) 轉成 rgba(r,g,b,a)，
  // 無效就 fallback 用 marker.color 原色（safari 等仍會接受 #rrggbb 但
  // alpha 失效，仍然比之前柔和因為層數減少）。
  const glowColor = hexToRgba(marker.color, 0.7) || marker.color;
  el.style.boxShadow = `
    0 0 8px ${glowColor},
    0 0 16px ${glowColor}
  `;
  el.style.filter = 'brightness(1.1)';
  
  // 添加點擊事件
  let clickTimeout: number | null = null;
  
  el.addEventListener('click', (e) => {
    e.stopPropagation();
    
    if (clickTimeout) {
      // 雙擊 - 根據 markerType 執行不同動作
      clearTimeout(clickTimeout);
      clickTimeout = null;
      
      if (marker.markerType === 'video' && marker.videoUrl) {
        window.open(marker.videoUrl, '_blank', 'width=1280,height=720,menubar=no,toolbar=no,location=no,status=no');
      }
    } else {
      // 單擊 - 根據 markerZoomEnabled 決定是否先 zoom in
      clickTimeout = window.setTimeout(() => {
        if (markerZoomEnabled) {
          // ─── altitude-aware flyTo framing ─────────────────────────
          //
          // Marker 用 Mapbox 原生 altitude (3D 錨點)，但 flyTo 的 center
          // 只能給 [lng, lat] (地面)。如果 altitude 高 (e.g. 100m)，相機
          // 把 ground 點框到正中央時，marker icon + popup 會被 3D 投影推
          // 到螢幕上方好幾百 px、甚至飛出畫面 → 使用者看不到 popup。
          //
          // 修法：用 padding.top 把「ground 點在 viewport 內的位置」往下
          // 推 ≈ altitude 投在螢幕的像素距離 × 2，落地時 altitude 點就剛好
          // 在 viewport 中央。
          //
          // 像素距離公式：
          //   altPxAtTarget = altitude_m × targetPpm × sin(pitch_rad)
          // 其中 targetPpm = currentPpm × 2^(targetZoom - currentZoom)
          // (ppm 隨 zoom 以 2 的指數變化，是 mercator 投影的性質)
          //
          // Cap 在 viewport 高度 80%，避免極端 altitude 把 ground 推到
          // 螢幕外（雖然 altitude 也跑出去，但這時連選 marker 都困難）。
          //
          // popup close 時 easeTo 還原 padding，避免之後 pan/zoom 都覺得
          // viewport 中心被偏移。
          // 對齊 location/folder marker (modelManager.flyToModel) 的固定
          // target：zoom 18 / pitch 60 / bearing -90 / duration 2000。
          // **唔再用** Math.max(getZoom()+2, 18) ——之前個 max() 喺已
          // 經 zoom 過 18 嗰陣會繼續每次 +2 越按越近，user 連按兩次就會
          // 衝到 zoom 22 度，呢度先 lock 住 18 同 folder marker 一致。
          // Altitude padding 仍然保留 (popup-as-object 框正 marker 至
          // viewport 中央)，但 targetPpm 改用「currentPpm × 2^(18 -
          // getZoom())」固定推算。
          const altitudeMetersForFly = marker.position?.altitude || 0;
          const TARGET_ZOOM = 18;
          const TARGET_PITCH = 60;
          const TARGET_BEARING = -90;
          const TARGET_DURATION = 2000;
          let padTopForAlt = 0;
          if (altitudeMetersForFly > 0) {
            const c = map.getCenter();
            const p1 = map.project([c.lng, c.lat]);
            const p2 = map.project([c.lng, c.lat + 1 / 111320]); // 1m 北
            const currentPpm = Math.abs(p2.y - p1.y);
            const targetPpm = currentPpm * Math.pow(2, TARGET_ZOOM - map.getZoom());
            const altPxAtTarget =
              altitudeMetersForFly * targetPpm * Math.sin(TARGET_PITCH * Math.PI / 180);
            padTopForAlt = Math.min(2 * altPxAtTarget, map.getCanvas().height * 0.8);
          }

          map.flyTo({
            center: [marker.position.lng, marker.position.lat],
            zoom: TARGET_ZOOM,
            pitch: TARGET_PITCH,
            bearing: TARGET_BEARING,
            padding: padTopForAlt > 0
              ? { top: padTopForAlt, bottom: 0, left: 0, right: 0 }
              : undefined,
            duration: TARGET_DURATION,
            essential: true
          });

          map.once('moveend', () => {
            const popup = showMarkerPopup(marker);
            if (popup && padTopForAlt > 0) {
              popup.once('close', () => {
                try {
                  map.easeTo({
                    padding: { top: 0, bottom: 0, left: 0, right: 0 },
                    duration: 250
                  });
                } catch { /* noop */ }
              });
            }
          });
        } else {
          // 直接顯示 popup
          showMarkerPopup(marker);
        }
        
        clickTimeout = null;
      }, 250);
    }
  });
  
  return el;
}

// 預載 Google Material Symbols icons
async function preloadMarkerIcons() {
  const iconNameMap: Record<string, string> = {
    'video': 'videocam',
    'device': 'sensors',
    'information': 'info',
    'location': 'place'
  };
  
  for (const [type, iconName] of Object.entries(iconNameMap)) {
    const iconSvgUrl = `https://fonts.gstatic.com/s/i/short-term/release/materialsymbolsoutlined/${iconName}/default/48px.svg`;
    
    try {
      const response = await fetch(iconSvgUrl);
      const svgText = await response.text();
      
      // 創建白色版本的 SVG
      const whiteSvg = svgText.replace(/fill="[^"]*"/g, 'fill="#ffffff"');
      const svgBlob = new Blob([whiteSvg], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = svgUrl;
      });
      
      iconImageCache.set(type, img);
      URL.revokeObjectURL(svgUrl);
    } catch (error) {
      console.error(`Failed to preload icon ${iconName}:`, error);
    }
  }
}

// 創建 icon texture 的同步函數 (使用預載的圖片)
// ========================================
// Mapbox 初始化設定
// ========================================

mapboxgl.accessToken = 'pk.eyJ1IjoibmtzY2hhbiIsImEiOiJjbWRwcThscnkwZm43MmtuN2xzdHlraHpkIn0.IF5OekMuKcufYYy9pHaJLw';

// 初始地圖中心（以香港為中心的地球視角）
const initialCenter: [number, number] = [114.1694, 22.3193];  // 香港經緯度

// Mapbox 初始縮放級別配置
// Zoom 級別說明：0 = 最遠（全球視圖），22 = 最近（街道級別）
// 建議範圍：10-15（城市級別），12-14（區域級別），8-10（國家級別），1-2（地球視角）
const INITIAL_ZOOM = 1.5; // 地球視角（可看到整個地球，以香港為中心）

// 初始化 Mapbox 地圖（使用 Dark 樣式）
const map = new mapboxgl.Map({
  container: 'mapboxContainer',
  style: 'mapbox://styles/mapbox/dark-v11',
  center: initialCenter,
  zoom: INITIAL_ZOOM,
  pitch: 0,
  bearing: 0,
  antialias: true,
  // 強制 mercator 投影：Mapbox v3 預設是 globe，但 Custom Layer (Three.js)
  // 用 MercatorCoordinate.fromLngLat() 算位置，跟 globe projection 不對應，
  // 會導致 frag model 在低 zoom 時偏移、且每次過渡狀態不同 → 偏移量不固定
  projection: { name: 'mercator' }
});


// ========================================
// 初始化跨域同步（localhost <-> 192.168.x.x）
// ========================================

// 跨域同步 (僅在開發環境啟用)
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  try {
    const crossSync = initCrossOriginSync({
      wsUrl: 'ws://localhost:3001',
      keys: ['echov-models', 'echov-folders'],
      autoSync: true,
      debug: true
    });
  } catch (error) {
    console.warn('⚠️ Cross-origin sync failed:', error);
  }
}

// 地圖加載完成後，設置建築物透明
map.on('style.load', () => {
  // 任何 style 變更（含 setStyle）都會 reset projection 回 v3 預設（globe）
  // Custom Layer (Three.js frag) 不支援 globe → 必須強制設回 mercator
  try { map.setProjection({ name: 'mercator' }); } catch (e) { /* ignore */ }
  
  // 等待樣式完全加載
  setTimeout(() => {
    try {
      const style = map.getStyle();
      if (!style || !style.layers) {
        console.warn("⚠️ Map style not fully loaded");
        return;
      }
      
      // 遍歷所有圖層，設置 3D 建築物透明度
      style.layers.forEach((layer: any) => {
        if (layer.type === 'fill-extrusion') {
          try {
            // 檢查圖層是否存在
            if (map.getLayer(layer.id)) {
              // 獲取當前的 paint 屬性
              const paint = map.getPaintProperty(layer.id, 'fill-extrusion-opacity');
              
              // 只有當屬性存在時才設置
              if (paint !== undefined || layer.paint?.['fill-extrusion-opacity'] !== undefined) {
                map.setPaintProperty(layer.id, 'fill-extrusion-opacity', 0.3);
              }
            }
          } catch (e) {
            console.warn(`  ⚠️ Cannot set opacity for layer ${layer.id}:`, e);
          }
        }
      });
      
    } catch (error) {
      console.error("❌ Error setting building opacity:", error);
    }
  }, 500); // 延遲 500ms 確保樣式完全加載
});

// ========================================
// Three.js 場景設定
// ========================================

// ========================================
// 從 Mapbox 投影矩陣提取 fovy（垂直視野角）
// ========================================
/**
 * 從 Mapbox 的投影矩陣中提取 fovy（垂直視野角）
 * 參考：cesium_ifc_nc_base 2 項目的實現方式
 * 
 * 在 Cesium 項目中，他們直接從相機獲取：
 *   camera.fov = CesiumMath.toDegrees(perspectiveFrustum.fovy);
 * 
 * 在 Mapbox 中，我們需要從投影矩陣中提取 fovy
 */
function extractFovyFromProjectionMatrix(projectionMatrix: THREE.Matrix4): number {
  const m = projectionMatrix.elements;
  
  // 嘗試從投影矩陣提取 fovy
  // 投影矩陣的結構：
  // [m[0]  m[1]  m[2]  m[3] ]
  // [m[4]  m[5]  m[6]  m[7] ]
  // [m[8]  m[9]  m[10] m[11]]
  // [m[12] m[13] m[14] m[15]]
  //
  // 對於透視投影矩陣：
  // m[5] = 1 / tan(fovy/2) / aspect
  // 所以 fovy = 2 * atan(1 / (m[5] * aspect))
  
  const m11 = m[5]; // 投影矩陣的 [1][1] 元素（索引 5）
  const m00 = m[0]; // 投影矩陣的 [0][0] 元素（索引 0）
  
  // 方法 1：使用 m[5]（如果有效）
  if (Math.abs(m11) > 0.0001) {
    // 計算 fovy（弧度）：fovy = 2 * atan(1 / m11)
    // 注意：m11 已經包含了 aspect 的影響
    const fovyRad = 2 * Math.atan(Math.abs(1 / m11));
    let fovyDeg = fovyRad * (180 / Math.PI);
    
    // 檢查結果是否合理
    if (fovyDeg >= 1 && fovyDeg <= 120) {
      return fovyDeg;
    }
  }
  
  // 方法 2：使用 m[0] 和 m[5]（如果 m[5] 無效）
  if (Math.abs(m00) > 0.0001 && Math.abs(m11) < 0.0001) {
    // 嘗試從 m[0] 提取（水平 FOV）
    const aspect = Math.abs(m00 / m11) || 1;
    const fovxRad = 2 * Math.atan(Math.abs(1 / m00));
    const fovyRad = 2 * Math.atan(Math.tan(fovxRad / 2) / aspect);
    let fovyDeg = fovyRad * (180 / Math.PI);
    
    if (fovyDeg >= 1 && fovyDeg <= 120) {
      return fovyDeg;
    }
  }
  
  // 備用方法：使用 Mapbox 的預設值或根據 zoom 估算
  // Mapbox GL JS 的預設垂直視野角約為 53.13 度
  // 但在高 zoom 時會變小，在低 zoom 時會變大
  // 我們可以根據 zoom 估算一個合理的值
  // 注意：這裡不能直接訪問 map，所以使用固定值
  const estimatedFovy = 53.13; // Mapbox 預設值
  
  // 無法從投影矩陣提取 fovy，使用預設值
  
  return estimatedFovy;
}

// 渲染用相機（由 Mapbox 控制投影矩陣）
const camera = new THREE.PerspectiveCamera(
  60,  // FOV（初始值，會在 render 中同步）
  window.innerWidth / window.innerHeight,
  0.00001,
  1000000
);

// LOD 計算用相機（用於 Fragments 視錐體計算）
// 這個相機的位置、旋轉和 fovy 會與 Mapbox 同步
const lodCamera = new THREE.PerspectiveCamera(
  70,  // FOV（使用最大 FOV，會在 render 中保持）
  window.innerWidth / window.innerHeight,
  0.00001,   // near 平面：極小值，避免模型太近時被視錐體裁剪 ✅
  1000000    // far 平面：100 萬米
);

// 固定鏡頭位置和視角的狀態
let cameraLocked = false;
let lockedCameraPosition: THREE.Vector3 | null = null;
let lockedCameraRotation: THREE.Euler | null = null;
let lockedCameraFov: number | null = null;

// 固定鏡頭位置的可視化標記（綠點）
let lockedCameraMarker: THREE.Mesh | null = null;

// 場景
const scene = new THREE.Scene();

// 添加光源
const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
directionalLight.position.set(0, 70, 100).normalize();
scene.add(directionalLight);

const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
scene.add(ambientLight);


// ========================================
// 多模型管理器
// ========================================

let modelManager: MultiModelManager;

// Folder 緩存（用於顯示 folder 名稱）
let folderCache: Map<string, { id: string; name: string }> = new Map();

// Folder 展開/收起狀態
let folderExpandedState: Map<string, boolean> = new Map();

// 早期創建的 model markers（在 modelManager 初始化之前）
let earlyModelMarkers: mapboxgl.Marker[] = [];
// 暴露到 window 以便 multiModelManager 可以訪問
(window as any).earlyModelMarkers = earlyModelMarkers;

// ========================================
// User-based Cache 系統
// ========================================

interface CachedUserData {
  userId: string;
  models: any[];
  folders: any[];
  timestamp: number;
  version: string;
}

const CACHE_VERSION = '1.0';
const CACHE_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 1 個月

/**
 * 獲取當前用戶 ID
 */
function getCurrentUserId(): string | null {
  try {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    if (userInfo.userId) return userInfo.userId;
  } catch {
    // ignore and try sessionStorage
  }
  try {
    const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    return sessionUser.userId || null;
  } catch {
    return null;
  }
}

/**
 * 從 localStorage 讀取用戶數據 cache
 */
function loadUserDataFromCache(userId: string): CachedUserData | null {
  try {
    const cacheKey = `echov-user-cache-${userId}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const data: CachedUserData = JSON.parse(cached);
    
    // 檢查版本和過期時間
    if (data.version !== CACHE_VERSION) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    const now = Date.now();
    if (now - data.timestamp > CACHE_EXPIRY) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    // 更新時間戳（延長過期時間）
    data.timestamp = now;
    localStorage.setItem(cacheKey, JSON.stringify(data));
    
    return data;
  } catch (error) {
    return null;
  }
}

/**
 * 將用戶數據保存到 localStorage cache
 */
function saveUserDataToCache(userId: string, models: any[], folders: any[]): void {
  try {
    const cacheKey = `echov-user-cache-${userId}`;
    const data: CachedUserData = {
      userId,
      models,
      folders,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };
    localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    // Cache 失敗不影響功能
  }
}

/**
 * 清除用戶 cache
 */
function clearUserCache(userId: string): void {
  try {
    const cacheKey = `echov-user-cache-${userId}`;
    localStorage.removeItem(cacheKey);
  } catch {
    // 忽略錯誤
  }
}

// ========================================
// 顏色控制器
// ========================================

let colorController: ModelColorController;

// ========================================
// 立即載入並顯示 Model Markers（在載入 3D 模型之前）
// ========================================

async function loadAndDisplayModelMarkers() {
  try {
    const { authClient } = await import('./authClient');
    const userId = getCurrentUserId();
    
    if (!userId || !authClient.isLoggedIn()) {
      return;
    }
    
    let userModels: any[] = [];
    let userFolders: any[] = [];
    
    // 嘗試從 cache 讀取
    const cachedData = loadUserDataFromCache(userId);
    if (cachedData) {
      userModels = cachedData.models;
      userFolders = cachedData.folders;
    } else {
      // 從 MongoDB 獲取
      userModels = await authClient.getUserModels();
      userFolders = await authClient.getUserFolders();
    }
    
    // 建立 folder 名稱映射
    const folderNameMap = new Map<string, string>();
    userFolders.forEach((folder: any) => {
      if (folder && folder.id && folder.name) {
        folderNameMap.set(folder.id, folder.name);
      }
    });
    
    // 按資料夾分組
    const modelsByFolder = new Map<string, any[]>();
    
    userModels.forEach(model => {
      const folderId = model.folder;
      const folderName = folderId ? (folderNameMap.get(folderId) || folderId) : 'No Folder';
      
      if (!modelsByFolder.has(folderName)) {
        modelsByFolder.set(folderName, []);
      }
      modelsByFolder.get(folderName)!.push(model);
    });
    
    // 為每個資料夾創建 marker（使用第一個模型的位置）
    // 注意：這裡創建的 markers 不會註冊到 modelManager，因為 modelManager 可能還沒初始化
    // 真正的 folder markers 會在 initializeModels() 中創建並註冊
    modelsByFolder.forEach((models, folderName) => {
      if (models.length === 0) return;
      
      // 按 ID 排序，使用第一個模型的位置
      const sortedModels = models.sort((a: any, b: any) => a.id.localeCompare(b.id));
      const firstModel = sortedModels[0];
      
      if (!firstModel.position || !firstModel.position.lng || !firstModel.position.lat) {
        return;
      }
      
      // 檢查 modelMarkersHidden 狀態（可能由 share page 設定）
      const shouldHide = (window as any).modelMarkersHidden || false;
      
      // 創建 point marker（彩色圓點）
      const pointEl = document.createElement('div');
      pointEl.className = 'model-point';
      pointEl.style.backgroundColor = firstModel.color || '#ff0000';
      pointEl.style.display = shouldHide ? 'none' : 'block';
      pointEl.title = folderName;
      pointEl.setAttribute('data-folder-name', folderName); // 添加標記以便後續識別
      const point = new mapboxgl.Marker(pointEl)
        .setLngLat([firstModel.position.lng, firstModel.position.lat])
        .addTo(map);
      
      // 創建 text marker（文字標籤）
      const markerEl = document.createElement('div');
      markerEl.className = 'model-marker';
      markerEl.style.display = shouldHide ? 'none' : 'block';
      markerEl.setAttribute('data-folder-name', folderName); // 添加標記以便後續識別
      const folderDiv = document.createElement('div');
      folderDiv.className = 'marker-folder';
      folderDiv.textContent = folderName;
      const statusDiv = document.createElement('div');
      statusDiv.className = 'marker-status';
      statusDiv.textContent = 'Normal';
      statusDiv.setAttribute('data-folder-id', folderName);
      markerEl.appendChild(folderDiv);
      markerEl.appendChild(statusDiv);
      const marker = new mapboxgl.Marker(markerEl, { offset: [0, -30] })
        .setLngLat([firstModel.position.lng, firstModel.position.lat])
        .addTo(map);
      
      // 保存到早期 markers 列表，以便後續清除
      earlyModelMarkers.push(point, marker);
      (window as any).earlyModelMarkers = earlyModelMarkers; // 同步到 window
    });
    
  } catch (error) {
    console.error('❌ Failed to load model markers:', error);
  }
}

async function initializeModels() {
  try {
    // 設定 Worker URL
    const workerUrl = new URL("/worker.mjs", import.meta.url).href;
    
    // 創建多模型管理器
    // 使用 lodCamera 用於 Fragments 視錐體計算，確保與 Mapbox 同步
    modelManager = new MultiModelManager(workerUrl, scene, lodCamera, map);
    
    // 立即暴露到全域（方便除錯）
    (window as any).modelManager = modelManager;
    
    // 從 Cache 或 MongoDB 獲取用戶模型和資料夾
    let configsToLoad: ModelConfig[] = [];
    
    try {
      const { authClient } = await import('./authClient');
      const userId = getCurrentUserId();
      
      let userModels: any[] = [];
      let userFolders: any[] = [];
      
      // 診斷期間：暫停 cache 讀取，永遠從 MongoDB 拿
      // 假設驗證：cache 路徑（同步 localStorage）載入過快觸發下游 race condition，
      // server fetch 較慢更接近序列執行，可消除偏移
      // 用 window.__USE_MODEL_CACHE = true 可重新啟用 cache（在 console 設後 reload）
      const useCache = (window as any).__USE_MODEL_CACHE === true;
      
      if (useCache && userId) {
        const cachedData = loadUserDataFromCache(userId);
        if (cachedData) {
          userModels = cachedData.models;
          userFolders = cachedData.folders;
          console.log(`📦 Loaded ${userModels.length} models from localStorage cache`);
        }
      }
      
      // cache 沒拿到（或被禁用） → 從 MongoDB 取
      if (userModels.length === 0) {
        console.log(`📡 Fetching models fresh from server (cache disabled=${!useCache})`);
        userModels = await authClient.getUserModels();
        userFolders = await authClient.getUserFolders();
        
        // 保存到 cache（即使本次跳過讀，仍寫入以便切回 cache 模式時可用）
        if (userId) {
          saveUserDataToCache(userId, userModels, userFolders);
        }
      }
      
      // 建立 folder cache
      folderCache.clear();
      userFolders.forEach((folder: any) => {
        if (folder && folder.id && folder.name) {
          folderCache.set(folder.id, { id: folder.id, name: folder.name });
        }
      });
      
      // 4. 使用 MongoDB 的模型數據
      if (userModels && Array.isArray(userModels) && userModels.length > 0) {
        configsToLoad = userModels;

        // 5. Batch-sign ALL R2 URLs in a single API call.
        //
        // Was: 60+ parallel /api/files/:id/url calls — slammed the
        // 100 req/s rate limiter (which counts both the OPTIONS
        // preflight AND the GET), produced a 429 storm, and looked
        // like CORS failures in dev tools.
        //
        // Now: one POST /api/files/batch-urls returns every URL.
        // Constant-time wrt model count, no rate-limit risk.
        const { getFileSignedUrlsBatch } = await import('./r2UploadClient');
        const idsNeedingSign = configsToLoad
          .filter((c) => c.fileId && !c.url)
          .map((c) => c.fileId as string);
        if (idsNeedingSign.length > 0) {
          const t0 = performance.now();
          try {
            const urlMap = await getFileSignedUrlsBatch(idsNeedingSign, 14400);
            let signed = 0;
            for (const config of configsToLoad) {
              if (config.fileId && !config.url) {
                const url = urlMap.get(config.fileId);
                if (url) {
                  config.url = url;
                  signed++;
                } else {
                  console.warn(`⚠️ Batch sign missed ${config.name} (fileId=${config.fileId})`);
                }
              }
            }
            console.log(`🔑 Got ${signed}/${idsNeedingSign.length} signed URLs (batch) in ${(performance.now() - t0).toFixed(0)}ms`);
          } catch (err) {
            console.error('❌ Batch signing failed:', err);
          }
        }
      }
      
    } catch (error) {
      console.error("❌ Failed to load from MongoDB:", error);
    }
    
    // 為每個模型添加 folder 的實際名稱（從 folderCache 獲取）
    configsToLoad = configsToLoad.map(config => {
      const updatedConfig: any = { ...config };
      
      if (config.folder && folderCache.has(config.folder)) {
        const folderInfo = folderCache.get(config.folder);
        updatedConfig.folderName = folderInfo!.name;  // 添加實際的 folder 名稱
      }
      
      // 如果是 share page，應用 shareVisibilityMap 中的 visible 狀態
      if ((window as any).shareVisibilityMap) {
        const shareVisibility = (window as any).shareVisibilityMap.get(config.id);
        if (shareVisibility !== undefined) {
          updatedConfig.visible = shareVisibility;
          console.log(`📋 [Share] Applying visibility for model ${config.id}: ${shareVisibility}`);
        }
      }
      
      return updatedConfig;
    });
    
    // 顯示進度條
    const progressBar = document.getElementById('loading-progress-bar');
    const progressFill = document.getElementById('loading-progress-fill');
    const statusText = document.getElementById('loading-status-text');
    const statusMessage = document.getElementById('loading-status-message');
    const statusPercentage = document.getElementById('loading-status-percentage');
    
    if (progressBar && statusText) {
      progressBar.style.display = 'block';
      statusText.style.display = 'block';
    }
    
    // 批量加載所有模型（使用原本的批量載入方式以提升效能）
    const totalModels = configsToLoad.length;
    
    // 設置初始進度
    if (progressFill) progressFill.style.width = '0%';
    if (statusPercentage) statusPercentage.textContent = '0%';
    if (statusMessage) statusMessage.textContent = `Loading models... (0/${totalModels})`;

    // Real progress wiring — loadModels invokes onProgress once per
    // (prefetch | parse) event. The bar moves smoothly from 0→50 %
    // during parallel download, then 50→100 % during parallel parse.
    // Coalesce updates with requestAnimationFrame so we don't thrash
    // layout 200× per second when 50 prefetches all finish at once.
    let pendingProgress: { percent: number; phase: string; name?: string; cached?: boolean } | null = null;
    let rafHandle: number | null = null;
    let prefetchCacheHits = 0;
    let prefetchTotal = 0;
    const flushProgress = () => {
      rafHandle = null;
      if (!pendingProgress) return;
      const { percent, phase, name, cached } = pendingProgress;
      pendingProgress = null;
      if (progressFill) progressFill.style.width = `${percent}%`;
      if (statusPercentage) statusPercentage.textContent = `${percent}%`;
      if (statusMessage) {
        // Phase-specific label. During prefetch we count cache hits
        // and surface them so the user knows the load is fast.
        let label: string;
        if (phase === 'prefetch') {
          // If every prefetch event so far has been a cache hit, the
          // bar is reading purely from IndexedDB → say "Cached".
          // Otherwise we mix downloads and hits → say "Downloading"
          // and append a hint about cache hits so the user knows the
          // cache IS doing something.
          if (cached && prefetchCacheHits === prefetchTotal) {
            label = 'Cached';
          } else if (prefetchCacheHits > 0) {
            label = `Downloading (${prefetchCacheHits} from cache)`;
          } else {
            label = 'Downloading';
          }
        } else {
          label = 'Loading';
        }
        statusMessage.textContent = name
          ? `${label} models... ${percent}% — ${name}`
          : `${label} models... ${percent}%`;
      }
    };

    await modelManager.loadModels(configsToLoad, (info) => {
      if (info.phase === 'prefetch') {
        prefetchTotal++;
        if (info.cached) prefetchCacheHits++;
      }
      pendingProgress = {
        percent: info.percent,
        phase: info.phase,
        name: info.currentName,
        cached: info.cached,
      };
      if (rafHandle === null) {
        rafHandle = requestAnimationFrame(flushProgress);
      }
    });
    // Make sure the very last update lands even if rAF was already
    // scheduled but the page paint hasn't happened yet.
    if (rafHandle !== null) cancelAnimationFrame(rafHandle);
    flushProgress();

    // 載入完成，設置進度為 100%
    if (progressFill) progressFill.style.width = '100%';
    if (statusPercentage) statusPercentage.textContent = '100%';
    if (statusMessage) statusMessage.textContent = `Loading models... (${totalModels}/${totalModels})`;
    
    // ========================================
    // 為所有模型設置 Fake Camera (防止元素消失)
    // ========================================
    setupFakeCamerasForAllModels(modelManager);
    
    // 狀態 API (僅在開發環境啟用)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      setTimeout(async () => {
        await StatusAPI.initializeStatuses();
      }, 1000);
    }
    
    // 隱藏進度條
    if (progressBar) progressBar.style.display = 'none';
    if (statusText) statusText.style.display = 'none';
    
    // 重新加載 folder 信息（確保名稱是最新的）
    try {
      const { AdminModelManager } = await import("./adminModelManager");
      const adminManager = new AdminModelManager();
      const adminFolders = await adminManager.loadFolders();
      folderCache.clear();
      adminFolders.forEach((folder) => {
        folderCache.set(folder.id, { id: folder.id, name: folder.name });
      });
    } catch (error) {
      console.warn("⚠️ Failed to reload folders:", error);
    }
    
    // 更新模型列表UI
    updateModelListUI();
    
    // 初始化顏色控制器（傳入 map 以便觸發重繪）
    colorController = new ModelColorController(modelManager, map);
    
    map.triggerRepaint();
    
  } catch (error) {
    console.error("❌ Error initializing models:", error);
  }
}


// ========================================
// Mapbox Custom Layer（Three.js 整合）
// ========================================

const customLayer: mapboxgl.CustomLayerInterface = {
  id: '3d-fragments-models',
  type: 'custom',
  renderingMode: '3d',
  
  onAdd: function (map, gl) {
    (window as any).__customLayerOnAddCount = ((window as any).__customLayerOnAddCount || 0) + 1;
    console.log(`[FRAG-LAYER] customLayer.onAdd call #${(window as any).__customLayerOnAddCount}`);
    try {
      initializeModels();
    } catch (error) {
      console.error('❌ Error in customLayer.onAdd:', error);
      throw error;
    }
  },
  
  render: function (gl, matrix) {
    // 等待模型管理器初始化
    if (!modelManager) return;
    
    // 更新動畫
    updateAnimations();
    
    // Mapbox 提供的投影矩陣
    const mapboxMatrix = new THREE.Matrix4().fromArray(matrix);
    
    // === 從 Mapbox 獲取相機參數 ===
    const mapCenter = map.getCenter();
    const mapZoom = map.getZoom();
    const mapPitch = map.getPitch();
    const mapBearing = map.getBearing();
    
    // === 從 Mapbox 投影矩陣提取 fovy（垂直視野角）===
    // 參考 cesium_ifc_nc_base 2：直接從相機獲取 fovy
    // 在 Mapbox 中，我們從投影矩陣提取
    const mapboxFovy = extractFovyFromProjectionMatrix(mapboxMatrix);
    const mapAspect = map.getCanvas().width / map.getCanvas().height;
    
    // === 計算相機位置（基於 Mapbox 參數）===
    // 這樣 Fragments 的視錐體才能與 Mapbox 的鏡頭完全一致
    // 
    // Mapbox 相機位置計算：
    // 1. 根據 zoom 計算相機高度（公尺）
    // 2. 轉換為 Mercator 座標
    // 3. 考慮 pitch 的影響調整高度
    
    // === 計算相機位置（基於 Mapbox 參數）===
    // 參考 Mapbox GL JS 內部實現和單模型版本的計算方式
    // 
    // Mapbox 相機位置計算：
    // 1. 根據 zoom 計算相機高度（公尺）
    // 2. 使用 MercatorCoordinate.fromLngLat() 直接設置高度
    // 3. 考慮 pitch 的影響調整高度
    
    const pitchRad = (mapPitch * Math.PI) / 180;
    const bearingRad = (mapBearing * Math.PI) / 180;
    
    // === 計算相機位置（基於 Mapbox 參數）===
    // 使用更直接的方法：直接使用 MercatorCoordinate.fromLngLat() 設置高度
    // 
    // Mapbox 相機位置計算：
    // 1. 根據 zoom 計算相機高度（公尺）
    // 2. 直接使用 MercatorCoordinate.fromLngLat() 轉換為 Mercator 座標（包含高度）
    // 3. 考慮 pitch 的影響調整高度
    
    // 計算相機高度（根據 zoom）
    // 使用與單模型版本相同的公式：altitude = 1000000 / 2^zoom
    const altitude = 1000000 / Math.pow(2, mapZoom);
    
    // 考慮 pitch 的影響
    // 當 pitch 增加時，相機需要更高的位置才能看到相同的區域
    const pitchFactor = Math.max(Math.cos(pitchRad), 0.1);
    let adjustedAltitude = altitude / pitchFactor;
    
    // 確保相機高度至少為 200 米
    // 這可以確保視錐體不會太接近地面，避免視角問題
    const MIN_CAMERA_HEIGHT = 200; // 最小相機高度（公尺）
    if (adjustedAltitude < MIN_CAMERA_HEIGHT) {
      adjustedAltitude = MIN_CAMERA_HEIGHT;
    }
    
    // 使用與 model 位置轉換相同的方式來計算相機位置
    // 參考 multiModelManager.ts 中 model 的位置轉換：
    // modelAsMercatorCoordinate = MercatorCoordinate.fromLngLat([lng, lat], altitude)
    // translateX = modelAsMercatorCoordinate.x
    // translateY = modelAsMercatorCoordinate.y
    // translateZ = modelAsMercatorCoordinate.z || 0
    //
    // 相機也使用相同的方式：
    const cameraAltitude = 50;  // 固定高度 600 米（與 model 的 altitude 參數類似）
    const cameraMercator = mapboxgl.MercatorCoordinate.fromLngLat(
      mapCenter,
      cameraAltitude  // 使用高度參數，與 model 的轉換方式完全一致
    );
    
    // 計算相機在 Three.js 世界座標系中的位置
    // 使用與 model 相同的轉換方式：
    // 根據 model 的轉換：translateX = Mercator.x, translateY = Mercator.y, translateZ = Mercator.z || 0
    // 
    // 但根據之前的代碼和實際測試，相機位置的映射是：
    // X = Mercator.x (東西方向) - 與 model 的 translateX 相同
    // Y = 直接使用高度（米）- 不使用 Mercator.z，直接使用 cameraAltitude
    // Z = Mercator.y (南北方向) - 與 model 的 translateY 相同
    //
    // 注意：Y 軸直接使用高度值（米），不使用 Mercator.z，這樣才能正確顯示
    const cameraPositionX = cameraMercator.x;  // X 軸：與 model 的 translateX 相同
    const cameraPositionY = cameraAltitude;   // Y 軸：直接使用高度（米），不使用 Mercator.z
    const cameraPositionZ = cameraMercator.y;  // Z 軸：與 model 的 translateY 相同
    
    // === 同步 fovy 到渲染相機（用於實際渲染）===
    // 如果鏡頭已固定，使用固定的 FOV；否則使用 Mapbox 的 FOV
    // mapboxFovy 已經在上面提取了
    let fov: number;
    if (cameraLocked && lockedCameraFov !== null) {
      fov = lockedCameraFov;  // 使用固定的 FOV
    } else {
      // 使用從 Mapbox 提取的 FOV，如果無效則使用預設值 70 度
      fov = (mapboxFovy > 1 && mapboxFovy < 120) ? mapboxFovy : 70;
    }
    
    if (Math.abs(camera.fov - fov) > 0.01) {
      camera.fov = fov;
      camera.aspect = mapAspect;
      camera.updateProjectionMatrix();
    }
    
    // === 同步 LOD 相機參數（用於 Fragments 視錐體計算）===
    // 這個相機會被所有 Fragments 模型使用（通過 useCamera）
    
    // 1. 同步 fovy、aspect
    // 如果鏡頭已固定，使用固定的 FOV；否則使用 Mapbox 的 FOV
    if (cameraLocked && lockedCameraFov !== null) {
      lodCamera.fov = lockedCameraFov;
    } else {
      lodCamera.fov = fov;
    }
    lodCamera.aspect = mapAspect;
    lodCamera.updateProjectionMatrix();
    
    // 2. 同步相機位置（用於視錐體計算）
    // 如果鏡頭已固定，使用固定的位置；否則使用 Mapbox 的位置
    if (cameraLocked && lockedCameraPosition) {
      lodCamera.position.copy(lockedCameraPosition);
    } else {
      // 位置必須精確匹配 Mapbox 的視角位置，使用 scale 轉換
      lodCamera.position.set(
        cameraPositionX,
        cameraPositionY,
        cameraPositionZ
      );
    }
    
    // 3. 同步相機旋轉（用於視錐體方向）
    // 如果鏡頭已固定，使用固定的旋轉；否則使用 lookAt 向下看
    if (cameraLocked && lockedCameraRotation) {
      lodCamera.rotation.copy(lockedCameraRotation);
      lodCamera.quaternion.setFromEuler(lockedCameraRotation);
    } else {
      // 讓相機永遠 lookAt 向下（-Y 方向），這樣視錐體才能覆蓋地面的模型
      // 重置旋轉（避免累積誤差）
      lodCamera.rotation.set(0, 0, 0);
      lodCamera.quaternion.set(0, 0, 0, 1);
      
      // 使用 lookAt 讓相機永遠向下看
      // 目標位置在相機正下方（Y 軸減小）
      const lookAtTarget = new THREE.Vector3(
        lodCamera.position.x,
        lodCamera.position.y - 1,  // 向下 1 米
        lodCamera.position.z
      );
      lodCamera.lookAt(lookAtTarget);
    }
    
    // 可選：設置相機朝向目標（用於視錐體可視化）
    // 這會讓視錐體更直觀地顯示相機看向哪裡
    // 但注意：這會影響相機的 rotation，所以我們在上面已經設置了 rotation
    // 如果使用 lookAt，需要重新計算 rotation
    
    // 4. 更新矩陣世界（Fragments 視錐體依賴這個）
    // 必須每幀調用，確保 Fragments 使用最新的相機參數
    lodCamera.updateMatrixWorld(true);
    
    // 調試：輸出相機位置信息
    if (!(window as any).__frameCount) (window as any).__frameCount = 0;
    (window as any).__frameCount++;
    
    // 5. 更新 Fragments（重新啟用）
    modelManager.getFragments().update(true);
    
    // 5.1. 強制確保所有模型始終可見（每幀確保）
    // ========================================
    // 暫時停用：測試是否影響著色
    // ========================================
    // modelManager.getAllModels().forEach((modelInfo) => {
    //   if (modelInfo.visible) {
    //     modelInfo.model.object.traverse((child) => {
    //       // 處理 LOD 對象
    //       if ((child as any).isLOD) {
    //         (child as any).autoUpdate = false;  // 禁用 LOD 自動更新
    //         child.visible = true;
    //         child.frustumCulled = false;
    //         
    //         // 強制 LOD 的所有子對象可見
    //         if (child.children) {
    //           child.children.forEach((lodChild) => {
    //             lodChild.visible = true;
    //             lodChild.frustumCulled = false;
    //             if (lodChild instanceof THREE.Mesh) {
    //               lodChild.frustumCulled = false;
    //               lodChild.visible = true;
    //             }
    //           });
    //         }
    //       }
    //       
    //       // 處理 Mesh 對象
    //       if (child instanceof THREE.Mesh) {
    //         child.frustumCulled = false;
    //         child.visible = true;
    //       }
    //     });
    //   }
    // });
    
    
    // 建立或取得 renderer
    if (!(this as any).renderer) {
      (this as any).renderer = new THREE.WebGLRenderer({
        canvas: map.getCanvas(),
        context: gl,
        antialias: true
      });
      (this as any).renderer.autoClear = false;
    }
    
    const renderer = (this as any).renderer;
    renderer.resetState();
    
    // 純診斷：在多個 frame log render 用的實際 transform
    // 對比 F5#1 vs F5#2 看 render 那刻的 transform 是否被改動了
    // 還支援手動 trigger：window.__dumpRender = true → 下一個 frame 印
    const __dbgFrame = (window as any).__frameCount;
    let __dbgShouldLog = (__dbgFrame === 1 || __dbgFrame === 30 || __dbgFrame === 60 || __dbgFrame === 120 || __dbgFrame === 300);
    if ((window as any).__dumpRender) {
      __dbgShouldLog = true;
      (window as any).__dumpRender = false;
    }
    if (__dbgShouldLog) {
      console.log(`[FRAG-RENDER frame=${__dbgFrame}] map zoom=${map.getZoom().toFixed(3)} center=(${map.getCenter().lng.toFixed(5)},${map.getCenter().lat.toFixed(5)}) pitch=${map.getPitch().toFixed(1)} bearing=${map.getBearing().toFixed(1)} projection=${(map as any).getProjection?.()?.name || '?'}`);
    }
    
    // 只渲染 Fragments 模型
    modelManager.getAllModels().forEach((modelInfo, modelId) => {
      if (!modelInfo.visible) return;
      if (modelInfo.modelType === 'gltf') return; // GLTF 由獨立 layer 處理
      
      const { model, transform } = modelInfo;
      
      if (__dbgShouldLog) {
        const obj = (model as any).object;
        const fmt = (n: number) => Number(n).toExponential(4);
        console.log(
          `[FRAG-RENDER frame=${__dbgFrame}] ${modelId} | ` +
          `objPos=(${fmt(obj.position.x)},${fmt(obj.position.y)},${fmt(obj.position.z)}) | ` +
          `t=(${fmt(transform.translateX)},${fmt(transform.translateY)},${fmt(transform.translateZ)}) | ` +
          `scale=${fmt(transform.scale)} | ` +
          `objVisible=${obj.visible} cfgVisible=${modelInfo.visible} | ` +
          `matrixAutoUpdate=${obj.matrixAutoUpdate} matrixWorldNeedsUpdate=${obj.matrixWorldNeedsUpdate}`
        );
      }
      
      // 暫時隱藏其他所有 Fragments 模型
      const originalVisibility = new Map<string, boolean>();
      modelManager.getAllModels().forEach((info, id) => {
        if (info.modelType === 'gltf') return;
        originalVisibility.set(id, (info.model as any).object.visible);
        (info.model as any).object.visible = (id === modelId && info.visible);
      });
      
      // 建立旋轉矩陣
      const rotationX = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(1, 0, 0),
        transform.rotateX
      );
      const rotationY = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(0, 1, 0),
        transform.rotateY
      );
      const rotationZ = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(0, 0, 1),
        transform.rotateZ
      );

      // 建立此模型的局部變換矩陣
      const localMatrix = new THREE.Matrix4()
        .makeTranslation(
          transform.translateX,
          transform.translateY,
          transform.translateZ
        )
        .scale(
          new THREE.Vector3(
            transform.scale,
            -transform.scale,  // Y 軸翻轉
            transform.scale
          )
        )
        .multiply(rotationX)
        .multiply(rotationY)
        .multiply(rotationZ);

      // 為此模型設置相機投影矩陣
      // 回退到原來的方案：直接使用 Mapbox 的投影矩陣
      // 方案 2 可能有問題導致模型消失，先使用原方案
      camera.projectionMatrix = mapboxMatrix.clone().multiply(localMatrix);
      
      // 渲染此模型
      renderer.render(scene, camera);
      
      // 恢復其他 Fragments 模型的可見性
      modelManager.getAllModels().forEach((info, id) => {
        if (info.modelType === 'gltf') return;
        (info.model as any).object.visible = originalVisibility.get(id) || false;
      });
      
      // 手機計數
    });
    
    // 渲染固定鏡頭位置的綠點標記（如果存在）
    if (lockedCameraMarker && lockedCameraMarker.parent === scene) {
      // 確保標記可見
      lockedCameraMarker.visible = true;
      // 單獨渲染標記（使用簡單的渲染方式）
      const markerRenderer = renderer;
      markerRenderer.render(scene, camera);
    }
    
    // ⚠️ Label overlay pass：確保 labels 永遠在最上面（在所有 frag 之上）
    //
    // 為什麼需要這個 pass？
    // - label 是 frag.object 的 child（見 handleCreateLabel）。
    // - 上面的 frag forEach 中，iteration N+1 會把 iteration N 的 frag.object.visible=false，
    //   而 Three.js 的 visibility 是繼承的，所以該 frag 內的 labels 也跟著隱藏，
    //   但是 framebuffer 已經有上一輪畫的 label 像素。
    // - iteration N+1 的 frag mesh 是 opaque，會直接覆蓋之前畫的 label 像素 → user 看到「label 在 frag 下」。
    //
    // 修法：在 frag forEach 之後，對每個 frag 做一輪「只畫該 frag 的 labels」的 render，
    //      用該 frag 的 localMatrix（因為 label 是 frag 的 child，必須用同一個 transform 才會在正確螢幕位置）。
    if (labelMap.size > 0) {
      // 先在這個 pass 開始前清掉 depth buffer，確保 labels（depthTest=false）
      // 也不會被前面 frag pass 的 depth state 影響
      gl.clear(gl.DEPTH_BUFFER_BIT);
      
      modelManager.getAllModels().forEach((labelOwnerInfo, labelOwnerId) => {
        if (labelOwnerInfo.modelType !== 'frag') return;
        
        const labelOwnerObj = (labelOwnerInfo.model as any).object as THREE.Object3D;
        
        // 找出此 frag.object 直接擁有的 labels
        const ownLabels: THREE.Object3D[] = [];
        labelMap.forEach((labelMesh) => {
          if (labelMesh.parent === labelOwnerObj) ownLabels.push(labelMesh);
        });
        if (ownLabels.length === 0) return;
        
        // 暫存 frag.object 跟其 children 的 visibility，並改成「只 labels 可見」
        const origFragVisible = labelOwnerObj.visible;
        const origChildVisible = new Map<THREE.Object3D, boolean>();
        labelOwnerObj.children.forEach(child => {
          origChildVisible.set(child, child.visible);
          child.visible = ownLabels.includes(child);
        });
        labelOwnerObj.visible = true;
        
        // 暫存其他 frag model 的 visibility，並隱藏（避免被一起畫）
        const otherOrigVis = new Map<string, boolean>();
        modelManager.getAllModels().forEach((info2, id2) => {
          if (info2.modelType !== 'frag') return;
          if (id2 === labelOwnerId) return;
          const obj = (info2.model as any).object as THREE.Object3D;
          otherOrigVis.set(id2, obj.visible);
          obj.visible = false;
        });
        
        // 用此 frag 的 localMatrix 投影（label 是 frag.object 的 child，必須用同一個 transform）
        const t = labelOwnerInfo.transform;
        const lrx = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1, 0, 0), t.rotateX);
        const lry = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), t.rotateY);
        const lrz = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 0, 1), t.rotateZ);
        const labelLocalMatrix = new THREE.Matrix4()
          .makeTranslation(t.translateX, t.translateY, t.translateZ)
          .scale(new THREE.Vector3(t.scale, -t.scale, t.scale))
          .multiply(lrx).multiply(lry).multiply(lrz);
        camera.projectionMatrix = mapboxMatrix.clone().multiply(labelLocalMatrix);
        
        // 再清一次 depth，這樣此 frag 的 labels 一定畫在最上
        gl.clear(gl.DEPTH_BUFFER_BIT);
        renderer.render(scene, camera);
        
        // 恢復 visibility
        origChildVisible.forEach((v, child) => { child.visible = v; });
        labelOwnerObj.visible = origFragVisible;
        otherOrigVis.forEach((v, id2) => {
          const info2 = modelManager.getAllModels().get(id2);
          if (info2) (info2.model as any).object.visible = v;
        });
      });
      
      // 處理「沒有對應 frag、直接加到 scene」的 label（fallback case in handleCreateLabel）
      const orphanLabels: THREE.Object3D[] = [];
      labelMap.forEach((labelMesh) => {
        if (labelMesh.parent === scene) orphanLabels.push(labelMesh);
      });
      if (orphanLabels.length > 0) {
        const origSceneChildVisible = new Map<THREE.Object3D, boolean>();
        scene.children.forEach(child => {
          origSceneChildVisible.set(child, child.visible);
          child.visible = orphanLabels.includes(child);
        });
        const identityMatrix = new THREE.Matrix4().identity();
        camera.projectionMatrix = mapboxMatrix.clone().multiply(identityMatrix);
        gl.clear(gl.DEPTH_BUFFER_BIT);
        renderer.render(scene, camera);
        origSceneChildVisible.forEach((v, child) => { child.visible = v; });
      }
    }
    
    // 注意：不要在 render 函數中調用 triggerRepaint()，這會導致無限循環
    // Mapbox 會自動調用 render 函數，無需手動觸發重繪
  }
};

// ========================================
// GLTF Custom Layer (獨立 - 參考 Mapbox 官方)
// ========================================
const gltfCustomLayer: mapboxgl.CustomLayerInterface = {
  id: '3d-gltf',
  type: 'custom',
  renderingMode: '3d',
  onAdd: function (map, gl) {
    try {
      this.camera = new THREE.Camera();
      this.scene = new THREE.Scene();
      const light1 = new THREE.DirectionalLight(0xffffff);
      light1.position.set(0, -70, 100).normalize();
      this.scene.add(light1);
      const light2 = new THREE.DirectionalLight(0xffffff);
      light2.position.set(0, 70, 100).normalize();
      this.scene.add(light2);
      this.map = map;
      this.renderer = new THREE.WebGLRenderer({
        canvas: map.getCanvas(),
        context: gl,
        antialias: true
      });
      this.renderer.autoClear = false;
    } catch (error) {
      console.error('❌ Error in gltfCustomLayer.onAdd:', error);
      throw error;
    }
  },
  render: function (gl, matrix) {
    if (!modelManager) return;
    
    // 更新動畫（與 frag layer 共用同一個動畫系統）
    updateAnimations();
    
    let gltfModels = Array.from(modelManager.getAllModels().entries())
      .filter(([_, info]) => info.modelType === 'gltf' && info.visible);
    
    if (gltfModels.length === 0) return;
    
    const m = new THREE.Matrix4().fromArray(matrix);
    this.renderer.resetState();
    
    gltfModels.forEach(([modelId, info]) => {
      const model = info.model as THREE.Group;
      const t = info.transform;
      
      // 清空 scene（移除所有 glTF models）
      const childrenToRemove = [...this.scene.children];
      childrenToRemove.forEach(child => {
        if (child.userData.modelId) {
          this.scene.remove(child);
        }
      });
      
      // 只加入當前要渲染的 model
      this.scene.add(model);
      
      const rx = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1,0,0), t.rotateX);
      const ry = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0,1,0), t.rotateY);
      const rz = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0,0,1), t.rotateZ);
      const l = new THREE.Matrix4()
        .makeTranslation(t.translateX, t.translateY, t.translateZ)
        .scale(new THREE.Vector3(t.scale, -t.scale, t.scale))
        .multiply(rx).multiply(ry).multiply(rz);
      
      this.camera.projectionMatrix = m.clone().multiply(l);
      this.renderer.render(this.scene, this.camera);
    });
    this.map.triggerRepaint();
  }
};

// ========================================
// 持續渲染機制（確保顏色更新立即顯示）
// ========================================

let continuousRenderEnabled = true;

function startContinuousRender() {
  function animate() {
    if (continuousRenderEnabled && map) {
      map.triggerRepaint();
    }
    
    requestAnimationFrame(animate);
  }
  animate();
}

// Labels 現在使用 THREE.Sprite，會被 Mapbox custom layer 自動渲染
// 不需要手動更新螢幕位置

// 在地圖載入後啟動持續渲染
map.on('load', () => {
  
  startContinuousRender();
});

// ========================================
// UI 更新函數
// ========================================

// ========================================
// Collapsible Section Header —— Model List / Marker List 兩個 section
// 都共用 .panel-section-header。一次 delegation listener，唔需要每次
// re-render 都重新綁。寫成 IIFE 確保 module 一 load 就 active；DOM 未
// build 完都唔怕，事件循環會 bubble 上 document。
// ========================================
(() => {
  if (typeof document === 'undefined') return;
  document.addEventListener('click', (e) => {
    const header = (e.target as HTMLElement)?.closest?.('.panel-section-header') as HTMLElement | null;
    if (!header) return;
    const targetId = header.dataset.target;
    if (!targetId) return;
    const section = document.getElementById(targetId);
    if (!section) return;

    const currentlyExpanded = section.dataset.expanded === 'true';
    const nextExpanded = !currentlyExpanded;
    section.dataset.expanded = nextExpanded ? 'true' : 'false';

    const body = section.querySelector('.panel-section-body') as HTMLElement | null;
    if (body) body.style.display = nextExpanded ? '' : 'none';

    const icon = header.querySelector('.section-toggle-icon');
    if (icon) icon.textContent = nextExpanded ? '▼' : '▶';
  });
})();

// ========================================
// Marker List Panel —— functional markers 喺 control panel 內以
// folder 分組展示，與 model list 共用 .folder-header / .model-item 樣式。
//
// 設計要點：
//   1. 每個 marker 一條 row，可以獨立 toggle 可見性 + fly to。
//   2. per-marker visibility 同全域 Functional Markers toggle 互動：
//      ─ 全域 off：所有 functional marker 隱藏，per-marker state 唔生效
//      ─ 全域 on ：per-marker state 決定每條 marker 顯示與否
//   3. folder 預設收起，model list 嗰邊 default 收起；marker list 自己
//      亦行同樣 pattern（folderExpandedState 屬 model list 專用，唔可以
//      shared 否則同名 folder 會互相影響）。
//   4. updateMarkerListUI 由 loadAndDisplayMarkers 完成後 call，所以
//      videoMarkers 已係最新；都會喺 marker 增刪改後重新觸發。
// ========================================
const markerListFolderExpanded = new Map<string, boolean>(); // key = folderId
const markerVisibilityState = new Map<string, boolean>();    // key = marker._id

function getMarkerIconChar(markerType?: string): string {
  switch (markerType) {
    case 'video':       return '🎥';
    case 'device':      return '📡';
    case 'information': return 'ℹ️';
    case 'location':    return '📍';
    default:            return '📍';
  }
}

/**
 * 把指定 marker 嘅 icon + label 設為顯示 / 隱藏。
 * markerElements map 入面 key 為 `_id` (icon) 同 `${_id}-label` (label)，
 * 直接攞返個 DOM element 改 display 就得，唔需要 remove() 後重 add。
 */
function applyMarkerItemVisibility(markerId: string, visible: boolean): void {
  const icon = markerElements.get(markerId);
  const label = markerElements.get(`${markerId}-label`);
  const display = visible ? '' : 'none';
  try { if (icon)  icon.getElement().style.display = display; } catch {}
  try { if (label) label.getElement().style.display = display; } catch {}
}

/**
 * 重新把 per-marker visibility state 全部套返落 DOM —— 用喺 global
 * Functional Markers toggle 由 OFF 切返 ON 後，唔好令所有 marker 都
 * 變晒可見而失去 per-marker 設定。
 */
function reapplyAllMarkerVisibility(): void {
  for (const marker of videoMarkers) {
    if (!marker._id) continue;
    const visible = markerVisibilityState.get(marker._id) !== false;
    applyMarkerItemVisibility(marker._id, visible);
  }
}
(window as any).reapplyAllMarkerVisibility = reapplyAllMarkerVisibility;

function updateMarkerListUI(): void {
  const list = document.getElementById('marker-list');
  const stats = document.getElementById('marker-stats');
  if (!list) return;

  list.innerHTML = '';

  if (!videoMarkers || videoMarkers.length === 0) {
    list.innerHTML = `<div style="padding: 10px; color: #888; font-size: 11px; text-align: center;">${i18n.t('panel.noMarkers')}</div>`;
    if (stats) stats.textContent = i18n.t('panel.markerStats', { visible: 0, total: 0 });
    return;
  }

  // 以 folder 分組 —— key 用 folderId 避免同名 folder collide；display name
  // 行 folderName → folderCache → folderId 嘅 fallback chain（同 model
  // list 一致），減少出 raw id 嘅機會。
  const byFolder = new Map<string, VideoMarker[]>();
  const NO_FOLDER_KEY = '__no_folder__';
  for (const m of videoMarkers) {
    const key = m.folderId || NO_FOLDER_KEY;
    if (!byFolder.has(key)) byFolder.set(key, []);
    byFolder.get(key)!.push(m);
  }

  let visibleCount = 0;
  let totalCount = 0;

  byFolder.forEach((markers, folderKey) => {
    const sample = markers[0];
    const folderName =
      sample?.folderName ||
      folderCache.get(folderKey)?.name ||
      (folderKey === NO_FOLDER_KEY ? 'No Folder' : folderKey);

    const isExpanded = markerListFolderExpanded.get(folderKey) === true;

    const header = document.createElement('div');
    header.className = 'folder-header';
    header.dataset.folderId = folderKey;
    header.dataset.folderName = folderName;
    header.innerHTML = `
      <span class="folder-toggle-icon">${isExpanded ? '▼' : '▶'}</span>
      <span class="folder-icon">📂</span>
      <span class="folder-name">${folderName}</span>
      <span class="folder-count">(${markers.length})</span>
    `;
    header.addEventListener('click', () => {
      markerListFolderExpanded.set(folderKey, !isExpanded);
      updateMarkerListUI();
    });
    list.appendChild(header);

    const container = document.createElement('div');
    container.className = 'folder-container';
    container.style.display = isExpanded ? 'block' : 'none';

    markers.forEach(marker => {
      totalCount++;
      if (!marker._id) return;

      const visible = markerVisibilityState.get(marker._id) !== false;
      if (visible) visibleCount++;

      const item = document.createElement('div');
      item.className = 'model-item';
      item.dataset.markerId = marker._id;

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = visible;
      checkbox.id = `marker-${marker._id}`;
      checkbox.addEventListener('change', () => {
        markerVisibilityState.set(marker._id!, checkbox.checked);
        applyMarkerItemVisibility(marker._id!, checkbox.checked);
        updateMarkerListUI();
      });

      const colorIndicator = document.createElement('span');
      colorIndicator.className = 'color-indicator';
      colorIndicator.style.backgroundColor = marker.color || '#888';

      const typeIcon = document.createElement('span');
      typeIcon.className = 'marker-type-icon';
      typeIcon.textContent = getMarkerIconChar(marker.markerType);
      typeIcon.title = marker.markerType || 'marker';

      const label = document.createElement('label');
      label.htmlFor = `marker-${marker._id}`;
      label.textContent = marker.name || marker.label || '(Unnamed marker)';

      const flyBtn = document.createElement('button');
      flyBtn.className = 'fly-btn';
      flyBtn.textContent = '>';
      flyBtn.title = i18n.t('button.flyToModel');
      flyBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        // 完全對齊地圖 marker icon click 嗰段 + folder/location marker
        // flyToModel 的固定 target：
        //   - zoom: 絕對 18（唔加 getZoom() 唔會越按越近）
        //   - pitch: 60, bearing: -90, duration: 2000
        //   - altitude > 0 時加 padding.top 把 elevated marker（連 popup
        //     當 3D 物件嗰個位）框落 viewport 中央
        const altMeters = marker.position?.altitude || 0;
        const TARGET_ZOOM = 18;
        const TARGET_PITCH = 60;
        const TARGET_BEARING = -90;
        const TARGET_DURATION = 2000;
        let padTop = 0;
        if (altMeters > 0) {
          const c = map.getCenter();
          const p1 = map.project([c.lng, c.lat]);
          const p2 = map.project([c.lng, c.lat + 1 / 111320]);
          const currentPpm = Math.abs(p2.y - p1.y);
          const targetPpm = currentPpm * Math.pow(2, TARGET_ZOOM - map.getZoom());
          const altPx = altMeters * targetPpm * Math.sin(TARGET_PITCH * Math.PI / 180);
          padTop = Math.min(2 * altPx, map.getCanvas().height * 0.8);
        }
        map.flyTo({
          center: [marker.position.lng, marker.position.lat],
          zoom: TARGET_ZOOM,
          pitch: TARGET_PITCH,
          bearing: TARGET_BEARING,
          padding: padTop > 0 ? { top: padTop, bottom: 0, left: 0, right: 0 } : undefined,
          duration: TARGET_DURATION,
          essential: true,
        });
      });

      item.appendChild(checkbox);
      item.appendChild(colorIndicator);
      item.appendChild(typeIcon);
      item.appendChild(label);
      item.appendChild(flyBtn);
      container.appendChild(item);
    });

    list.appendChild(container);
  });

  if (stats) {
    stats.textContent = i18n.t('panel.markerStats', { visible: visibleCount, total: totalCount });
  }
}
(window as any).updateMarkerListUI = updateMarkerListUI;

function updateModelListUI() {
  const modelList = document.getElementById('model-list');
  if (!modelList || !modelManager) {
    return;
  }
  
  // 清空列表
  modelList.innerHTML = '';
  
  // 獲取所有模型並按資料夾分組
  const allModels = Array.from(modelManager.getAllModels().entries());
  const modelsByFolder = new Map<string, Array<[string, any]>>();
  const modelsWithoutFolder: Array<[string, any]> = [];
  
  allModels.forEach(([modelId, modelInfo]) => {
    const folder = modelInfo.config.folder;
    if (folder) {
      if (!modelsByFolder.has(folder)) {
        modelsByFolder.set(folder, []);
      }
      modelsByFolder.get(folder)!.push([modelId, modelInfo]);
    } else {
      modelsWithoutFolder.push([modelId, modelInfo]);
    }
  });
  
  // 渲染有資料夾的模型
  modelsByFolder.forEach((models, folderId) => {
    // 獲取資料夾名稱（優先使用模型中的 folderName,否則從緩存或使用 ID）
    const firstModel = models[0]?.[1];
    const folderNameFromModel = firstModel?.config?.folderName;
    const folderInfo = folderCache.get(folderId);
    const folderName = folderNameFromModel || (folderInfo ? folderInfo.name : folderId);
    
    // 獲取展開狀態（默認為收起）
    const isExpanded = folderExpandedState.get(folderId) === true;
    
    // 創建資料夾容器（包含標題和狀態）
    const folderHeaderWrapper = document.createElement('div');
    folderHeaderWrapper.className = 'folder-wrapper';
    
    // 創建資料夾標題
    const folderHeader = document.createElement('div');
    folderHeader.className = 'folder-header';
    folderHeader.dataset.folderId = folderId;
    folderHeader.dataset.folderName = folderName; // 保存真實的 folder 名稱
    folderHeader.dataset.flyHint = i18n.t('folder.doubleClickToFly');
    folderHeader.innerHTML = `
      <span class="folder-toggle-icon">${isExpanded ? '▼' : '▶'}</span>
      <span class="folder-icon">📁</span>
      <span class="folder-name">${folderName}</span>
      <span class="folder-count">(${models.length})</span>
    `;
    
    // 點擊 folder 標題：切換展開/收起
    folderHeader.addEventListener('click', (e) => {
      e.stopPropagation();
      const currentState = folderExpandedState.get(folderId) === true;
      folderExpandedState.set(folderId, !currentState);
      updateModelListUI();
    });
    
    // 雙擊 folder 標題：飛到第一個 model 的位置
    folderHeader.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      if (models.length > 0) {
        const firstModel = models[0][1];
        const firstModelId = models[0][0];
        if (firstModel && modelManager) {
          modelManager.flyToModel(firstModelId);
        }
      }
    });
    
    // 創建第二行：viewer 按鈕 + status
    const folderActionsRow = document.createElement('div');
    folderActionsRow.className = 'folder-actions-row';
    
    // 創建 viewer 按鈕
    const viewerButton = document.createElement('button');
    viewerButton.className = 'folder-viewer-btn';
    viewerButton.innerHTML = '📁 Viewer';
    viewerButton.style.cssText = `
      padding: 4px 12px;
      background: rgba(68, 211, 222, 0.2);
      color: #44d3de;
      border: 1px solid #44d3de;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
      font-weight: 600;
      transition: all 0.2s;
      flex-shrink: 0;
    `;
    viewerButton.onmouseover = () => {
      viewerButton.style.background = 'rgba(68, 211, 222, 0.3)';
    };
    viewerButton.onmouseout = () => {
      viewerButton.style.background = 'rgba(68, 211, 222, 0.2)';
    };
    viewerButton.addEventListener('click', (e) => {
      e.stopPropagation();
      console.log('👁️ Viewer button clicked for folder:', folderName);

      // Build the MINIMAL payload — id / name / fileId / modelType only.
      // No pre-signed URL: with 50+ frag files a single signed R2 URL is
      // ~1.5 KB, so 50 of them stuffed into a query string blow past
      // Safari's URL limit and nginx's header buffer, causing the
      // browser to drop the connection (-> "server unexpectedly dropped
      // the connection"). The viewer re-signs every file itself using
      // the same /api/files/:id/url endpoint the share flow already
      // uses, so the payload here only needs file identity.
      const modelsInFolder: Array<{
        id: string;
        name: string;
        fileId?: string;
        modelType?: string;
      }> = [];

      console.log('📦 Collecting models from folder, total:', models.length);
      for (const [modelId, modelInfo] of models) {
        modelsInFolder.push({
          id: modelId,
          name: modelInfo.config.name,
          fileId: (modelInfo.config as any).fileId,
          modelType:
            (modelInfo as any).modelType ||
            (modelInfo as any).config?.modelType ||
            'frag',
        });
      }

      console.log('📦 Total models to pass to viewer:', modelsInFolder.length);
      const viewerUrl = openViewerWithPayload(modelsInFolder, folderName);
      console.log('🌐 Opening viewer URL:', viewerUrl);
      const newWindow = window.open(viewerUrl, '_blank', 'width=1400,height=900');
      if (!newWindow) {
        console.error('❌ Failed to open new window - popup might be blocked');
        alert('無法打開 Viewer 視窗，請檢查瀏覽器是否阻擋了彈出視窗');
      } else {
        console.log('✅ Viewer window opened successfully');
      }
    });
    
    // 創建狀態顯示元素
    const folderStatus = document.createElement('div');
    folderStatus.className = 'folder-status';
    folderStatus.dataset.panelFolderId = folderName; // 使用 folderName 作為識別
    folderStatus.textContent = 'Normal'; // 默認狀態
    
    folderActionsRow.appendChild(viewerButton);
    folderActionsRow.appendChild(folderStatus);
    
    folderHeaderWrapper.appendChild(folderHeader);
    folderHeaderWrapper.appendChild(folderActionsRow);
    modelList.appendChild(folderHeaderWrapper);
    
    // 創建資料夾容器（根據展開狀態顯示/隱藏）
    const folderContainer = document.createElement('div');
    folderContainer.className = 'folder-container';
    folderContainer.style.display = isExpanded ? 'block' : 'none';
    
    // 為資料夾內的每個模型創建列表項
    models.forEach(([modelId, modelInfo]) => {
      const item = createModelListItem(modelId, modelInfo);
      folderContainer.appendChild(item);
    });
    
    modelList.appendChild(folderContainer);
  });
  
  // 渲染沒有資料夾的模型
  if (modelsWithoutFolder.length > 0) {
    modelsWithoutFolder.forEach(([modelId, modelInfo]) => {
      const item = createModelListItem(modelId, modelInfo);
      modelList.appendChild(item);
    });
  }
  
  // 更新統計信息
  const stats = document.getElementById('model-stats');
  if (stats) {
    const total = modelManager.getModelCount();
    const visible = modelManager.getVisibleModels().length;
    stats.textContent = i18n.t('panel.stats', { visible, total });
  }
}

function createModelListItem(modelId: string, modelInfo: any): HTMLElement {
  const item = document.createElement('div');
  item.className = 'model-item';
  item.dataset.modelId = modelId;
  
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.checked = modelInfo.visible;
  checkbox.id = `model-${modelId}`;
  checkbox.addEventListener('change', () => {
    modelManager.toggleModel(modelId);
    updateModelListUI();
  });
  
  const label = document.createElement('label');
  label.htmlFor = `model-${modelId}`;
  // 使用翻譯後的模型名稱
  label.textContent = i18n.translateModelName(modelId, modelInfo.config.name);
  
  const flyButton = document.createElement('button');
  flyButton.className = 'fly-btn';
  flyButton.textContent = '>';
  flyButton.title = i18n.t('button.flyToModel');
  flyButton.addEventListener('click', () => {
    modelManager.flyToModel(modelId);
  });
  
  const colorIndicator = document.createElement('span');
  colorIndicator.className = 'color-indicator';
  colorIndicator.style.backgroundColor = modelInfo.config.color || '#888';
  
  item.appendChild(checkbox);
  item.appendChild(colorIndicator);
  item.appendChild(label);
  item.appendChild(flyButton);
  
  return item;
}

// ========================================
// 更新所有 UI 文本（語言切換時調用）
// ========================================

function updateAllUIText() {
  // 更新所有帶有 data-i18n 屬性的元素
  document.querySelectorAll('[data-i18n]').forEach(element => {
    const key = element.getAttribute('data-i18n');
    if (key) {
      element.textContent = i18n.t(key);
    }
  });
  
  // 更新所有帶有 data-i18n-title 屬性的元素
  document.querySelectorAll('[data-i18n-title]').forEach(element => {
    const key = element.getAttribute('data-i18n-title');
    if (key) {
      element.setAttribute('title', i18n.t(key));
    }
  });
  
  // 更新模型列表（會自動更新 folder 提示文字）
  updateModelListUI();
  
  // 更新語言按鈕文字
  const languageText = document.getElementById('languageText');
  if (languageText) {
    languageText.textContent = i18n.getCurrentLanguage() === 'en' ? 'ENG' : '繁中';
  }
  
  // 更新下拉選單的選中狀態
  const languageOptions = document.querySelectorAll('.language-option');
  languageOptions.forEach(option => {
    const lang = option.getAttribute('data-lang');
    if (lang === i18n.getCurrentLanguage()) {
      option.classList.add('selected');
    } else {
      option.classList.remove('selected');
    }
  });
  
  // 更新所有 folder header 的提示文字
  document.querySelectorAll('.folder-header').forEach(header => {
    const folderHeader = header as HTMLElement;
    folderHeader.dataset.flyHint = i18n.t('folder.doubleClickToFly');
  });
  
  // 更新透明化按鈕文字（根據當前狀態）- 不使用翻譯系統
  const transparencyText = document.getElementById('transparencyText');
  if (transparencyText) {
    transparencyText.textContent = isTransparent ? 'RED QUEEN MODE: ON' : 'RED QUEEN MODE: OFF';
  }
}

// ========================================
// 地圖載入完成後的設定
// ========================================

map.on('load', async () => {
  try {
    // 添加 3D 建築物圖層
    const layers = map.getStyle().layers;
    let firstSymbolId;
    for (const layer of layers) {
      if (layer.type === 'symbol') {
        firstSymbolId = layer.id;
        break;
      }
    }
    
    map.addLayer({
      'id': '3d-buildings',
      'source': 'composite',
      'source-layer': 'building',
      'filter': ['==', 'extrude', 'true'],
      'type': 'fill-extrusion',
      'minzoom': 15,
      'paint': {
        'fill-extrusion-color': [
          'interpolate',
          ['linear'],
          ['get', 'height'],
          0, '#4a90e2',
          50, '#5ca3e5',
          100, '#7fb3d5',
          200, '#a2c4e8'
        ],
        'fill-extrusion-height': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15, 0,
          15.05, ['get', 'height']
        ],
        'fill-extrusion-base': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15, 0,
          15.05, ['get', 'min_height']
        ],
        'fill-extrusion-opacity': 0.6
      }
    }, firstSymbolId);
    
    // 立即載入所有 markers（functional markers + model markers）
    await Promise.all([
      loadAndDisplayMarkers(),
      loadAndDisplayModelMarkers()
    ]);
    
    // 添加 Fragments 圖層（會觸發 initializeModels，載入模型）
    map.addLayer(customLayer);
    
    // 添加 GLTF 圖層
    map.addLayer(gltfCustomLayer);
  } catch (error) {
    console.error('❌ Map initialization error:', error);
    throw error;
  }
  
  // 暴露全域 API
  (window as any).EchoV = {
    // 模型管理（直接暴露 modelManager 對象）
    modelManager: modelManager,
    showModel: (id: string) => modelManager?.showModel(id),
    hideModel: (id: string) => modelManager?.hideModel(id),
    toggleModel: (id: string) => modelManager?.toggleModel(id),
    showAllModels: () => modelManager?.showAllModels(),
    hideAllModels: () => modelManager?.hideAllModels(),
    flyToModel: (id: string) => modelManager?.flyToModel(id),
    
    // 模型查詢
    getModel: (id: string) => modelManager?.getModel(id),
    getModelInfo: (id: string) => modelManager?.getModelInfo(id),
    getAllModels: () => modelManager?.getAllModels(),
    
    // 相機視錐體可視化控制
    // 固定/解鎖鏡頭位置和視角
    lockCamera: () => {
      cameraLocked = true;
      lockedCameraPosition = lodCamera.position.clone();
      lockedCameraRotation = lodCamera.rotation.clone();
      // 確保 FOV 是合理的值（如果當前 FOV 異常，使用 70 度）
      lockedCameraFov = (lodCamera.fov > 1 && lodCamera.fov < 120) ? lodCamera.fov : 70;
      
      // 創建綠點標記固定鏡頭位置（使用較大的尺寸以便觀察）
      if (lockedCameraMarker) {
        scene.remove(lockedCameraMarker);
      }
      const markerGeometry = new THREE.SphereGeometry(100, 16, 16);  // 增大尺寸到 100，更容易看到
      const markerMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ff00,  // 綠色
        transparent: false,  // 不透明，更容易看到
        opacity: 1.0
      });
      lockedCameraMarker = new THREE.Mesh(markerGeometry, markerMaterial);
      lockedCameraMarker.position.copy(lockedCameraPosition);
      lockedCameraMarker.name = 'locked-camera-marker';
      lockedCameraMarker.visible = true;
      scene.add(lockedCameraMarker);
      
      // 觸發重繪
      map.triggerRepaint();
    },
    
    unlockCamera: () => {
      cameraLocked = false;
      lockedCameraPosition = null;
      lockedCameraRotation = null;
      lockedCameraFov = null;
      
      // 移除綠點標記
      if (lockedCameraMarker) {
        scene.remove(lockedCameraMarker);
        lockedCameraMarker = null;
      }
    },
    
    toggleCameraLock: () => {
      if (cameraLocked) {
        (window as any).EchoV.unlockCamera();
      } else {
        (window as any).EchoV.lockCamera();
      }
    },
    
    // 調試相機和視錐體
    debugCamera: () => {
      const mapCenter = map.getCenter();
      const mapZoom = map.getZoom();
      const mapPitch = map.getPitch();
      const mapBearing = map.getBearing();
      
      return {
        camera: lodCamera,
        mapbox: {
          center: mapCenter,
          zoom: mapZoom,
          pitch: mapPitch,
          bearing: mapBearing
        }
      };
    },
    getModelIds: () => modelManager?.getModelIds(),
    getVisibleModels: () => modelManager?.getVisibleModels(),
    
    // 場景訪問
    map,
    camera,
    scene,
    
    // 語言切換
    i18n,
    getLanguage: () => i18n.getCurrentLanguage(),
    setLanguage: (lang: Language) => {
      i18n.setLanguage(lang);
      updateAllUIText();
    },
    toggleLanguage: () => {
      const newLang = i18n.toggleLanguage();
      updateAllUIText();
      return newLang;
    },
    
    // 切換 3D 建築
    toggle3DBuildings: (visible?: boolean) => {
      const layer = map.getLayer('3d-buildings');
      if (!layer) {
        console.warn("❌ 3D buildings layer does not exist");
        return false;
      }
      
      const currentVisibility = map.getLayoutProperty('3d-buildings', 'visibility');
      const newVisibility = visible !== undefined 
        ? (visible ? 'visible' : 'none')
        : (currentVisibility === 'visible' ? 'none' : 'visible');
      
      map.setLayoutProperty('3d-buildings', 'visibility', newVisibility);
      
      return newVisibility === 'visible';
    },
    
    // 切換 Markers
    toggleVideoMarkers: (visible?: boolean) => {
      const newVisibility = visible !== undefined ? visible : !markersVisible;
      markersVisible = newVisibility;
      
      // 切換 marker icons 的可見性
      const iconLayer = map.getLayer('marker-icons');
      if (iconLayer) {
        map.setLayoutProperty('marker-icons', 'visibility', newVisibility ? 'visible' : 'none');
      }
      
      // 切換 marker labels 的可見性
      const labelLayer = map.getLayer('marker-labels');
      if (labelLayer) {
        map.setLayoutProperty('marker-labels', 'visibility', newVisibility ? 'visible' : 'none');
      }
      
      // 如果隱藏,關閉 popup
      if (!newVisibility && currentMarkerPopup) {
        currentMarkerPopup.remove();
        currentMarkerPopup = null;
      }
      
      return newVisibility;
    },
    
    // 顯示 Video Markers
    showVideoMarkers: () => {
      return (window as any).EchoV.toggleVideoMarkers(true);
    },
    
    // 隱藏 Video Markers
    hideVideoMarkers: () => {
      return (window as any).EchoV.toggleVideoMarkers(false);
    },
    
    // 設置 Fragments 模型的原點位置
    setModelOrigin: (modelId: string, x?: number, y?: number, z?: number) => {
      if (!modelManager) {
        console.error("❌ Model manager not initialized");
        return null;
      }
      
      const modelInfo = modelManager.getAllModels().get(modelId);
      if (!modelInfo || modelInfo.modelType === 'gltf') {
        console.error(`❌ GLTF does not support this operation`);
        return null;
      }
      
      const model = modelManager.getModel(modelId);
      if (!model) {
        console.error(`❌ Model not found: ${modelId}`);
        return null;
      }
      
      const oldPos = (model as any).object.position.clone();
      if (x !== undefined) (model as any).object.position.x = x;
      if (y !== undefined) (model as any).object.position.y = y;
      if (z !== undefined) (model as any).object.position.z = z;
      
      // 強制更新矩陣
      (model as any).object.updateMatrix();
      (model as any).object.updateMatrixWorld(true);
      
      map.triggerRepaint();
      return (model as any).object.position.clone();
    },
    
    // 調整 Fragments 模型的原點位置（相對移動）
    adjustModelOrigin: (modelId: string, dx?: number, dy?: number, dz?: number) => {
      if (!modelManager) {
        console.error("❌ Model manager not initialized");
        return null;
      }
      
      const modelInfo = modelManager.getAllModels().get(modelId);
      if (!modelInfo || modelInfo.modelType === 'gltf') {
        console.error(`❌ GLTF does not support this operation`);
        return null;
      }
      
      const model = modelManager.getModel(modelId);
      if (!model) {
        console.error(`❌ Model not found: ${modelId}`);
        return null;
      }
      
      const oldPos = (model as any).object.position.clone();
      if (dx !== undefined) (model as any).object.position.x += dx;
      if (dy !== undefined) (model as any).object.position.y += dy;
      if (dz !== undefined) (model as any).object.position.z += dz;
      
      // 強制更新矩陣
      (model as any).object.updateMatrix();
      (model as any).object.updateMatrixWorld(true);
      
      map.triggerRepaint();
      return (model as any).object.position.clone();
    },
    
    // 獲取 Fragments 模型的原點位置
    getModelOrigin: (modelId: string) => {
      if (!modelManager) {
        console.error("❌ Model manager not initialized");
        return null;
      }
      
      const modelInfo = modelManager.getAllModels().get(modelId);
      if (!modelInfo || modelInfo.modelType === 'gltf') {
        console.error(`❌ GLTF does not support this operation`);
        return null;
      }
      
      const model = modelManager.getModel(modelId);
      if (!model) {
        console.error(`❌ Model not found: ${modelId}`);
        return null;
      }
      
      return (model as any).object.position.clone();
    },
    
    // 獲取模型的世界位置（考慮父對象的變換）
    getModelWorldPosition: (modelId: string) => {
      if (!modelManager) {
        console.error("❌ Model manager not initialized");
        return null;
      }
      
      const modelInfo = modelManager.getAllModels().get(modelId);
      if (!modelInfo || modelInfo.modelType === 'gltf') {
        console.error(`❌ GLTF does not support this operation`);
        return null;
      }
      
      const model = modelManager.getModel(modelId);
      if (!model) {
        console.error(`❌ Model not found: ${modelId}`);
        return null;
      }
      
      const worldPosition = new THREE.Vector3();
      (model as any).object.getWorldPosition(worldPosition);
      return worldPosition;
    },
    
    // 透明度控制
    setAllModelsTransparency: async (opacity: number) => {
      if (!modelManager) {
        console.error("❌ Model manager not initialized");
        return;
      }
      await modelManager.setAllModelsTransparency(opacity);
      map.triggerRepaint();
    },
    
    // 顏色控制 API
    colorController: () => colorController,
    
    // 設置元素顏色
    setElementsColor: async (modelId: string, localIds: number[], color: string, opacity = 1) => {
      if (!colorController) {
        console.error("❌ Color controller not initialized");
        return;
      }
      await colorController.setElementsColor(modelId, localIds, { color, opacity });
    },
    
    // 重置元素顏色
    resetElementsColor: async (modelId: string, localIds?: number[]) => {
      if (!colorController) {
        console.error("❌ Color controller not initialized");
        return;
      }
      await colorController.resetElementsColor(modelId, localIds);
    },
    
    // 設置類別顏色
    setCategoryColor: async (modelId: string, category: string, color: string, opacity = 1) => {
      if (!colorController) {
        console.error("❌ Color controller not initialized");
        return;
      }
      await colorController.setCategoryColor(modelId, category, { color, opacity });
    },
    
    // 獲取模型類別
    getCategories: async (modelId: string) => {
      if (!colorController) {
        console.error("❌ Color controller not initialized");
        return [];
      }
      return await colorController.getCategories(modelId);
    },
    
    // 獲取類別元素
    getCategoryElements: async (modelId: string, category: string) => {
      if (!colorController) {
        console.error("❌ Color controller not initialized");
        return [];
      }
      return await colorController.getCategoryElements(modelId, category);
    },
    
    // 重置所有顏色
    resetAllColors: async () => {
      if (!colorController) {
        console.error("❌ Color controller not initialized");
        return;
      }
      await colorController.resetAll();
    },
    
    // 獲取著色統計
    getColoredStats: () => {
      if (!colorController) {
        console.error("❌ Color controller not initialized");
        return {};
      }
      return colorController.getColoredStats();
    },
    
    // 渲染控制
    enableContinuousRender: () => {
      continuousRenderEnabled = true;
    },
    disableContinuousRender: () => {
      continuousRenderEnabled = false;
    },
    isContinuousRenderEnabled: () => continuousRenderEnabled,
    
    // Cache 管理
    clearCache: () => {
      const userId = getCurrentUserId();
      if (userId) {
        clearUserCache(userId);
      }
    },
    getCacheInfo: () => {
      const userId = getCurrentUserId();
      if (!userId) return null;
      const cached = loadUserDataFromCache(userId);
      if (!cached) return null;
      return {
        modelsCount: cached.models.length,
        foldersCount: cached.folders.length,
        timestamp: new Date(cached.timestamp).toLocaleString(),
        age: Math.round((Date.now() - cached.timestamp) / 1000) + 's'
      };
    }
  };
});

// ========================================
// UI 事件綁定
// ========================================

// 顯示所有模型按鈕
document.getElementById('show-all-btn')?.addEventListener('click', () => {
  modelManager?.showAllModels();
  updateModelListUI();
});

// 隱藏所有模型按鈕
document.getElementById('hide-all-btn')?.addEventListener('click', () => {
  modelManager?.hideAllModels();
  updateModelListUI();
});

// 切換控制面板
document.getElementById('toggle-panel-btn')?.addEventListener('click', () => {
  const panel = document.getElementById('control-panel');
  if (panel) {
    panel.classList.toggle('collapsed');
  }
});

// 語言切換按鈕和下拉選單
const languageToggleBtn = document.getElementById('languageToggleBtn');
const languageDropdown = document.getElementById('languageDropdown');
const languageText = document.getElementById('languageText');

if (languageToggleBtn && languageDropdown) {
  // 點擊按鈕切換下拉選單
  languageToggleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    languageDropdown.classList.toggle('active');
  });

  // 點擊選項切換語言
  const languageOptions = languageDropdown.querySelectorAll('.language-option');
  languageOptions.forEach(option => {
    option.addEventListener('click', (e) => {
      const lang = (e.currentTarget as HTMLElement).getAttribute('data-lang') as Language;
      
      // 切換語言
      i18n.setLanguage(lang);
      updateAllUIText();
      
      // 更新按鈕文字
      if (languageText) {
        languageText.textContent = lang === 'en' ? 'ENG' : '繁中';
      }
      
      // 更新選中狀態
      languageOptions.forEach(opt => opt.classList.remove('selected'));
      (e.currentTarget as HTMLElement).classList.add('selected');
      
      // 關閉下拉選單
      languageDropdown.classList.remove('active');
    });
  });

  // 點擊其他地方關閉下拉選單
  document.addEventListener('click', (e) => {
    if (!languageToggleBtn.contains(e.target as Node) && !languageDropdown.contains(e.target as Node)) {
      languageDropdown.classList.remove('active');
    }
  });
}

// 透明化按鈕
let isTransparent = false;

// 暴露 RED QUEEN MODE 狀態到全局（供 ModelColorController 使用）
(window as any).isRedQueenMode = false;

document.getElementById('transparencyBtn')?.addEventListener('click', async () => {
  if (!modelManager) return;
  
  const btn = document.getElementById('transparencyBtn');
  const textSpan = document.getElementById('transparencyText');
  
  isTransparent = !isTransparent;
  
  // 更新全局狀態
  (window as any).isRedQueenMode = isTransparent;
  
  if (isTransparent) {
    // 使用 ISOLATE_TRANSPARENT_GUIDE.md 的方法設置透明
    // 80% 透明 = opacity 0.2
    await modelManager.setAllModelsTransparency(0.2);
    if (btn) btn.classList.add('active');
    if (textSpan) textSpan.textContent = 'RED QUEEN MODE: ON';
  } else {
    // 使用 RESET_FUNCTION_GUIDE.md 的方法重置
    // opacity 1.0 = 完全不透明,會觸發 resetHighlight
    await modelManager.setAllModelsTransparency(1.0);
    if (btn) btn.classList.remove('active');
    if (textSpan) textSpan.textContent = 'RED QUEEN MODE';
  }
  
  map.triggerRepaint();
});

// 隱藏/顯示建築物按鈕
let buildingsHidden = false;
document.getElementById('buildingToggleBtn')?.addEventListener('click', () => {
  buildingsHidden = !buildingsHidden;
  
  const btn = document.getElementById('buildingToggleBtn');
  const textSpan = document.getElementById('buildingToggleText');
  
  toggleBuildings();
});

function toggleBuildings() {
  const btn = document.getElementById('buildingToggleBtn');
  const textSpan = document.getElementById('buildingToggleText');
  
  // 獲取所有圖層並隱藏所有建築物相關圖層
  const style = map.getStyle();
  if (style && style.layers) {
    style.layers.forEach((layer: any) => {
      // 隱藏所有 fill-extrusion 類型的圖層（3D 建築物）
      if (layer.type === 'fill-extrusion') {
        if (map.getLayer(layer.id)) {
          map.setLayoutProperty(
            layer.id,
            'visibility',
            buildingsHidden ? 'none' : 'visible'
          );
        }
      }
      // 隱藏所有名稱包含 building 的圖層
      if (layer.id.toLowerCase().includes('building')) {
        if (map.getLayer(layer.id)) {
          map.setLayoutProperty(
            layer.id,
            'visibility',
            buildingsHidden ? 'none' : 'visible'
          );
        }
      }
    });
  }
  
  // 也隱藏自定義的 3d-buildings 圖層（如果存在）
  if (map.getLayer('3d-buildings')) {
    map.setLayoutProperty(
      '3d-buildings',
      'visibility',
      buildingsHidden ? 'none' : 'visible'
    );
  }
  
  if (buildingsHidden) {
    // 隱藏狀態 - 使用對比色 (紅色)
    if (btn) btn.classList.add('active');
    if (textSpan) textSpan.textContent = 'Buildings: OFF';
  } else {
    // 顯示狀態 - 恢復原色 (青色)
    if (btn) btn.classList.remove('active');
    if (textSpan) textSpan.textContent = 'Buildings';
  }
  
  map.triggerRepaint();
}

// 隱藏/顯示模型 Markers 按鈕
// 使用全局變量，以便 share page 可以設定初始狀態
let modelMarkersHidden = (window as any).modelMarkersHidden || false;
document.getElementById('modelMarkerToggleBtn')?.addEventListener('click', () => {
  modelMarkersHidden = !modelMarkersHidden;
  (window as any).modelMarkersHidden = modelMarkersHidden;
  
  const btn = document.getElementById('modelMarkerToggleBtn');
  const textSpan = document.getElementById('modelMarkerToggleText');
  
  // 獲取所有 model-marker 元素 (文字標籤)
  const modelMarkers = document.querySelectorAll('.model-marker');
  modelMarkers.forEach((marker) => {
    const htmlMarker = marker as HTMLElement;
    if (modelMarkersHidden) {
      htmlMarker.style.display = 'none';
    } else {
      htmlMarker.style.display = 'block';
    }
  });
  
  // 獲取所有 model-point 元素 (紅點)
  const modelPoints = document.querySelectorAll('.model-point');
  modelPoints.forEach((point) => {
    const htmlPoint = point as HTMLElement;
    if (modelMarkersHidden) {
      htmlPoint.style.display = 'none';
    } else {
      htmlPoint.style.display = 'block';
    }
  });
  
  // 獲取所有 mapboxgl-marker 元素 (Mapbox 創建的 marker 容器)
  const mapboxMarkers = document.querySelectorAll('.mapboxgl-marker');
  let modelMarkerContainerCount = 0;
  
  mapboxMarkers.forEach((marker) => {
    const htmlMarker = marker as HTMLElement;
    // 只隱藏包含 model-marker 或 model-point 的 marker (不包含 functional markers)
    const hasModelMarker = htmlMarker.querySelector('.model-marker');
    const hasModelPoint = htmlMarker.querySelector('.model-point');
    const hasFunctionalMarker = htmlMarker.querySelector('.functional-marker-icon') || htmlMarker.querySelector('.functional-marker-label');
    
    if ((hasModelMarker || hasModelPoint) && !hasFunctionalMarker) {
      modelMarkerContainerCount++;
      if (modelMarkersHidden) {
        htmlMarker.style.display = 'none';
      } else {
        htmlMarker.style.display = 'block';
      }
    }
  });
  
  
  if (modelMarkersHidden) {
    // 隱藏狀態 - 使用對比色 (紅色)
    if (btn) btn.classList.add('active');
    if (textSpan) textSpan.textContent = 'Model Markers: OFF';
  } else {
    // 顯示狀態 - 恢復原色 (青色)
    if (btn) btn.classList.remove('active');
    if (textSpan) textSpan.textContent = 'Model Markers';
  }
});

// 隱藏/顯示 Functional Markers 按鈕
let functionalMarkersHidden = false;
document.getElementById('functionalMarkerToggleBtn')?.addEventListener('click', () => {
  functionalMarkersHidden = !functionalMarkersHidden;
  
  const btn = document.getElementById('functionalMarkerToggleBtn');
  const textSpan = document.getElementById('functionalMarkerToggleText');
  
  // 切換 functional markers 的可見性
  // 直接查找 functional-marker-icon 和 functional-marker-label 元素
  let functionalMarkerCount = 0;
  
  // 切換 icon 元素
  const functionalIcons = document.querySelectorAll('.functional-marker-icon');
  functionalIcons.forEach((icon) => {
    const iconEl = icon as HTMLElement;
    // 找到它的 mapboxgl-marker 父容器
    const markerContainer = iconEl.closest('.mapboxgl-marker') as HTMLElement;
    if (markerContainer) {
      functionalMarkerCount++;
      if (functionalMarkersHidden) {
        markerContainer.style.display = 'none';
      } else {
        markerContainer.style.display = 'block';
      }
    }
  });
  
  // 切換 label 元素
  const functionalLabels = document.querySelectorAll('.functional-marker-label');
  functionalLabels.forEach((label) => {
    const labelEl = label as HTMLElement;
    // 找到它的 mapboxgl-marker 父容器
    const markerContainer = labelEl.closest('.mapboxgl-marker') as HTMLElement;
    if (markerContainer) {
      if (functionalMarkersHidden) {
        markerContainer.style.display = 'none';
      } else {
        markerContainer.style.display = 'block';
      }
    }
  });

  // 全域 toggle 由 OFF 變返 ON 之後，per-marker hide 個別 marker 嘅
  // 設定會被上面 `display = 'block'` 直接 override —— 必須 re-apply
  // 一次 per-marker visibility，否則 user 喺 panel 個別收起嘅 marker
  // 會「死灰復燃」。
  if (!functionalMarkersHidden) {
    reapplyAllMarkerVisibility();
  }
  
  
  // 更新按鈕狀態
  if (functionalMarkersHidden) {
    if (btn) btn.classList.add('active');
    if (textSpan) textSpan.textContent = 'Functional Markers: OFF';
  } else {
    if (btn) btn.classList.remove('active');
    if (textSpan) textSpan.textContent = 'Functional Markers';
  }
});

// 切換 Marker Zoom 功能按鈕
document.getElementById('markerZoomToggleBtn')?.addEventListener('click', () => {
  applyMarkerZoomEnabled(!markerZoomEnabled);
});

/**
 * 設定 markerZoomEnabled 的 single source of truth — 同時改 state、按鈕
 * class、按鈕文字。click handler 和 share state restore 都走這條，免得
 * UI 跟 state 走唔同步。
 *
 * 一定要 export 到 window，因為 main_share.ts 用 `import './main_multi_model'`
 * 載入此模組後仍然係另一個 module scope，無辦法直接寫入 module-local
 * `markerZoomEnabled` 變數。
 */
function applyMarkerZoomEnabled(enabled: boolean): void {
  markerZoomEnabled = !!enabled;
  const btn = document.getElementById('markerZoomToggleBtn');
  const textSpan = document.getElementById('markerZoomToggleText');
  if (markerZoomEnabled) {
    if (btn) btn.classList.remove('active');
    if (textSpan) textSpan.textContent = 'Marker Zoom: ON';
  } else {
    if (btn) btn.classList.add('active');
    if (textSpan) textSpan.textContent = 'Marker Zoom: OFF';
  }
}
(window as any).applyMarkerZoomEnabled = applyMarkerZoomEnabled;

// 初始化時更新 UI 文本
updateAllUIText();


// ========================================
// Share 功能
// ========================================

/**
 * 捕獲當前場景狀態
 */
function captureSceneState() {
  // 1. 相機位置
  const center = map.getCenter();
  const camera = {
    lng: center.lng,
    lat: center.lat,
    zoom: map.getZoom(),
    pitch: map.getPitch(),
    bearing: map.getBearing()
  };
  
  // 2. Mapbox style
  const mapboxStyle = map.getStyle()?.name || 'mapbox://styles/mapbox/dark-v11';
  
  // 3. 模型狀態（使用當前實際的顯示狀態，而不是數據庫中的狀態）
  const models: any[] = [];
  if (modelManager) {
    modelManager.getAllModels().forEach((modelInfo, modelId) => {
      models.push({
        id: modelId,
        name: modelInfo.config.name,
        visible: modelInfo.visible !== false, // 使用當前實際的 visible 狀態
        color: modelInfo.config.color || '#ff0000',
        folder: modelInfo.config.folder,
        folderName: modelInfo.config.folderName
      });
    });
  }
  
  // 4. 建築物可見性
  const buildingsVisible = !buildingsHidden;
  
  // 5. Functional Markers 可見性
  const functionalMarkersVisibleState = !functionalMarkersHidden;
  
  // 6. Model Markers 可見性 (紅點和文字標籤)
  const modelMarkersVisibleState = !modelMarkersHidden;
  
  // 7. RED QUEEN MODE 狀態
  const redQueenMode = isTransparent;

  // 8. Marker Zoom 功能 toggle — 控制按 functional marker 是否會先 flyTo
  //    再開 popup。Update Scene 必須一齊 push 出去，share 出去後使用者
  //    才能控制這個行為（share view 入面無 toolbar 可改）。
  const markerZoom = markerZoomEnabled;

  const sceneState = {
    camera,
    mapboxStyle,
    models,
    buildingsVisible,
    functionalMarkersVisible: functionalMarkersVisibleState,
    modelMarkersVisible: modelMarkersVisibleState,
    redQueenMode,
    markerZoomEnabled: markerZoom,
    timestamp: Date.now()
  };
  
  return sceneState;
}

/**
 * 把一個已保存的 sceneState 套用回目前 app 的 viewer 上 — 用於「Load
 * Scene from Share」流程：使用者在 Manage Shares 揀返一條 share，把該
 * share 的鏡頭角度／marker zoom／marker visibility／ghost mode／per-model
 * visibility 等狀態一鍵 sync 落 app，調好之後可以再 Update Scene push
 * 返同一條 share，完全唔需要重新 create share / 換 URL。
 *
 * 設計取捨：
 *   - **不重 load model**：appviewer 已經有 model，share state 入面的
 *     model list 只用來 sync visibility，不會 trigger model 重新下載。
 *   - **靠 click 模擬 toggle**：buildings / functional markers / model
 *     markers / red queen mode 各自有 click handler，handler 已經內建
 *     UI 同步（按鈕 class、text、實際效果），這裡只需要對比 desired vs
 *     current，差異時 click 一下就同步。比起獨立 import 每個 helper、
 *     維護成本低好多。
 *   - **markerZoomEnabled** 走 applyMarkerZoomEnabled（已 single-source-
 *     of-truth），不依賴點擊 hack。
 *   - **animated 預設 true**：flyTo 1 秒，視覺上有「轉場」感、user 知道
 *     發生緊乜。
 *
 * 已 export 到 window.applySceneToApp 給 app.html 的 Manage Shares
 * "Load Scene" 按鈕 handler 用。
 */
function applySceneToApp(state: any, opts: { animated?: boolean } = {}): void {
  if (!state || typeof state !== 'object') return;
  const animated = opts.animated !== false; // default true

  // ─── Camera ────────────────────────────────────────────────────────────
  if (state.camera) {
    const c = state.camera;
    try {
      if (animated) {
        map.flyTo({
          center: [c.lng, c.lat],
          zoom: c.zoom,
          pitch: c.pitch,
          bearing: c.bearing,
          duration: 1000,
          essential: true,
        });
      } else {
        map.jumpTo({
          center: [c.lng, c.lat],
          zoom: c.zoom,
          pitch: c.pitch,
          bearing: c.bearing,
        });
      }
    } catch (err) {
      console.warn('[applySceneToApp] camera apply failed', err);
    }
  }

  // ─── Marker Zoom toggle ────────────────────────────────────────────────
  if (state.markerZoomEnabled !== undefined) {
    applyMarkerZoomEnabled(!!state.markerZoomEnabled);
  }

  // ─── Buildings (3D buildings layer) ────────────────────────────────────
  // buildingsHidden === false means visible. state.buildingsVisible === true
  // means desired visible. 不同就 click。
  if (state.buildingsVisible !== undefined) {
    const desiredVisible = !!state.buildingsVisible;
    const currentVisible = !buildingsHidden;
    if (desiredVisible !== currentVisible) {
      document.getElementById('buildingToggleBtn')?.click();
    }
  }

  // ─── Functional Markers visibility ────────────────────────────────────
  if (state.functionalMarkersVisible !== undefined) {
    const desiredVisible = !!state.functionalMarkersVisible;
    const currentVisible = !functionalMarkersHidden;
    if (desiredVisible !== currentVisible) {
      document.getElementById('functionalMarkerToggleBtn')?.click();
    }
  }

  // ─── Model Markers (folder dots / labels) visibility ──────────────────
  if (state.modelMarkersVisible !== undefined) {
    const desiredVisible = !!state.modelMarkersVisible;
    const currentVisible = !modelMarkersHidden;
    if (desiredVisible !== currentVisible) {
      document.getElementById('modelMarkerToggleBtn')?.click();
    }
  }

  // ─── RED QUEEN MODE (model transparency) ──────────────────────────────
  if (state.redQueenMode !== undefined) {
    const desiredOn = !!state.redQueenMode;
    if (desiredOn !== isTransparent) {
      document.getElementById('transparencyBtn')?.click();
    }
  }

  // ─── Per-model visibility ──────────────────────────────────────────────
  // share state 內 models[].visible 對應目前 modelManager 的 model id；
  // 若 modelManager 未 ready 就 skip（user 仍會 sync 到 camera + toggle，
  // 之後手動調 model visibility 也可）。
  // MultiModelManager 用 showModel / hideModel 兩條 API，沒 setVisibility
  // 統一 setter。
  if (Array.isArray(state.models) && modelManager) {
    const mgr = modelManager as any;
    state.models.forEach((m: any) => {
      if (!m || !m.id) return;
      try {
        const info = mgr.getAllModels?.().get?.(m.id);
        if (!info) return;
        const desiredVisible = m.visible !== false;
        if (info.visible !== desiredVisible) {
          if (desiredVisible) mgr.showModel?.(m.id);
          else mgr.hideModel?.(m.id);
        }
      } catch (err) {
        console.warn(`[applySceneToApp] per-model apply failed for ${m.id}`, err);
      }
    });
  }
}
(window as any).applySceneToApp = applySceneToApp;

/**
 * 創建分享鏈接
 */
async function createShareLink() {
  try {
    // 詢問用戶輸入 label 和 description
    const label = prompt('Enter a label for this share (optional):') || '';
    const description = prompt('Enter a description (optional):') || '';
    
    // 詢問用戶選擇期限
    const expiryOptions = prompt(
      'Select expiry period:\n' +
      '1 - 1 day\n' +
      '2 - 7 days\n' +
      '3 - 30 days (default)\n' +
      '4 - 90 days\n' +
      '5 - 1 year\n' +
      '6 - Never expire\n' +
      'Enter number (1-6):'
    ) || '3';
    
    let expiryDays: number | null = 30; // 預設 30 天
    switch (expiryOptions) {
      case '1': expiryDays = 1; break;
      case '2': expiryDays = 7; break;
      case '3': expiryDays = 30; break;
      case '4': expiryDays = 90; break;
      case '5': expiryDays = 365; break;
      case '6': expiryDays = null; break; // null 表示永不過期
      default: expiryDays = 30; break;
    }
    
    // 捕獲場景狀態
    const sceneState = captureSceneState();
    
    // 獲取當前用戶信息
    const userStr = sessionStorage.getItem('user');
    const token = sessionStorage.getItem('token');
    
    if (!token) {
      alert('請先登錄');
      return;
    }
    
    let userId, username;
    if (userStr) {
      const user = JSON.parse(userStr);
      userId = user.id;
      username = user.username;
    }
    
    // 發送到後端
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    let authServerUrl;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      authServerUrl = 'http://localhost:3002';
    } else {
      authServerUrl = `${protocol}//${hostname}`;
    }
    
    const response = await fetch(`${authServerUrl}/api/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        userId,
        username,
        sceneState,
        label,
        description,
        expiryDays
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [Share] API error:', errorText);
      throw new Error(`Failed to create share link (${response.status})`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to create share link');
    }
    
    // 生成分享鏈接
    const shareUrl = `${protocol}//${hostname}/share.html?id=${data.shareId}`;
    
    // 複製到剪貼板 (使用 fallback 方法)
    let copied = false;
    try {
      await navigator.clipboard.writeText(shareUrl);
      copied = true;
    } catch (clipboardError) {
      console.warn('⚠️ Clipboard API failed, using fallback:', clipboardError);
      
      // Fallback: 創建臨時 textarea
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        document.execCommand('copy');
        copied = true;
      } catch (execError) {
        console.error('❌ Fallback copy also failed:', execError);
      }
      
      document.body.removeChild(textarea);
    }
    
    if (copied) {
      alert(`✅ Share link copied to clipboard!\n\n${shareUrl}\n\nThis link can be accessed by anyone without login.`);
    } else {
      // 如果複製失敗,顯示鏈接讓用戶手動複製
      const userCopy = prompt('Please manually copy this share link:', shareUrl);
      if (userCopy === null) {
        alert(`Share link created:\n\n${shareUrl}`);
      }
    }
    
  } catch (error) {
    console.error('❌ Failed to create share link:', error);
    alert('Failed to create share link: ' + (error as Error).message);
  }
}

// Share 按鈕事件已在 index.html 的 user menu logic 中綁定，這裡不需要重複綁定

// ========================================
// Video Marker 功能
// ========================================

async function loadAndDisplayMarkers() {
  try {
    const { authClient } = await import('./authClient');
    
    if (!authClient.isLoggedIn()) {
      return;
    }

    const response = await authClient.fetchWithAuth('/api/markers');
    if (!response.ok) {
      console.error('❌ Failed to load markers');
      return;
    }

    videoMarkers = await response.json();
    
    // 清除舊的 mapbox markers
    markerElements.forEach(marker => marker.remove());
    markerElements.clear();
    
    // 預載 Material Symbols icons
    await preloadMarkerIcons();
    
    // 為每個 marker 創建 Mapbox Marker (icon + label)
    for (const marker of videoMarkers) {
      if (!marker._id) continue;
      
      // 創建 marker icon 元素
      const iconEl = await createMarkerElement(marker);
      
      // altitude 直接交給 Mapbox 內建的 Marker.altitude (v3.11+)
      // —— marker 真正錨在 3D 世界 (lng, lat, altitude) 那個點，
      // 縮放 / pan / pitch / bearing 全自動跟著走，不用手動投影。
      // 比起寫死 pixel offset 或 sin(pitch)*ppm 都精準（Mapbox 內部
      // 用真正的 mercator 矩陣，pespective 也含進去）。
      const altitudeMeters = marker.position.altitude || 0;

      const mapboxMarker = new mapboxgl.Marker({
        element: iconEl,
        anchor: 'bottom',
        offset: [0, -10],            // icon 底邊距 anchor 點 10px (跟 icon 高 21px 配)
        altitude: altitudeMeters     // 公尺，Mapbox 會自動投影到 3D
      } as any)
      .setLngLat([marker.position.lng, marker.position.lat])
      .addTo(map);

      markerElements.set(marker._id, mapboxMarker);
      
      // 創建 label 元素
      const labelEl = document.createElement('div');
      labelEl.className = 'floating-label functional-marker-label';
      labelEl.textContent = marker.label || marker.name;
      labelEl.style.background = 'rgba(0,0,0,0.8)';
      labelEl.style.color = marker.color; // 使用 marker 的顏色
      labelEl.style.padding = '4px 10px'; // 縮小 30%
      labelEl.style.borderRadius = '4px';
      labelEl.style.fontSize = '12px'; // 縮小字體
      labelEl.style.fontWeight = 'bold';
      labelEl.style.whiteSpace = 'nowrap';
      labelEl.style.pointerEvents = 'none'; // label 不可點擊，避免遮擋 icon
      labelEl.style.border = `1px solid ${marker.color}`;
      
      // Neon light 效果 for label (調低到 0.7 強度與 marker icon 一致)
      // 原本 text-shadow 3 層 + box-shadow 2 層全部 full alpha — 一條
      // viewport 內幾個 functional marker label 並排就一片發光糊。
      // 改為 2 層 text-shadow + 1 層 box-shadow，全部走 0.7 alpha；保留
      // 底下黑色 drop shadow 維持可讀性。
      const labelGlow = hexToRgba(marker.color, 0.7) || marker.color;
      labelEl.style.textShadow = `
        0 0 4px ${labelGlow},
        0 0 8px ${labelGlow}
      `;
      labelEl.style.boxShadow = `
        0 0 8px ${labelGlow},
        0 4px 8px rgba(0, 0, 0, 0.4)
      `;
      
      // label 也錨在同一個 3D altitude，加固定 36px 額外 offset 浮在 icon 上方
      // —— 因為 altitude 由 Mapbox 處理，這裡只剩「離 icon 上方的固定 px」
      // 不再需要把 altitude*2 加進來。
      const labelHeightOffset = 36;
      labelEl.style.transform = `translate(-50%, -100%) translateY(-${labelHeightOffset}px)`;

      const labelMarker = new mapboxgl.Marker({
        element: labelEl,
        anchor: 'bottom',
        offset: [0, -labelHeightOffset],
        altitude: altitudeMeters
      } as any)
      .setLngLat([marker.position.lng, marker.position.lat])
      .addTo(map);
      
      // 儲存 label marker (用於後續清除)
      markerElements.set(`${marker._id}-label`, labelMarker);

      // 套用 per-marker visibility —— 例如用戶之前喺 panel 揀咗收起呢
      // 條 marker，loadAndDisplayMarkers 重新建立 DOM 之後都要恢復。
      // 第一次見到嘅 marker（無 entry）預設可見。
      const desiredVisible = markerVisibilityState.get(marker._id) !== false;
      if (!desiredVisible) {
        applyMarkerItemVisibility(marker._id, false);
      }
    }

    // markers 全部 spawn 完先 render panel，避免每 add 一條就 re-render
    updateMarkerListUI();

  } catch (error) {
    console.error('❌ Error loading markers:', error);
  }
}

// ========================================
// Marker 點擊事件處理 (使用 DOM overlay)
// ========================================

function showMarkerPopup(marker: any): mapboxgl.Popup {
  // 同一個 marker 不重複開 popup —— 同一個 marker 連按多下，仍只看到一個
  // popup。不同 marker 各自的 popup 可同時存在（沒有 global mutex）。
  //
  // key 選用優先序：
  //   1. marker._id           (functional marker — load marker DB row 的 _id)
  //   2. folder:<folderName>  (folder/model marker — 沒 _id，用 folderName)
  //   3. name:<name>          (其他臨時 marker — fallback 用 name)
  const popupKey: string =
    (marker._id && `fn:${marker._id}`) ||
    (marker.folderName && `folder:${marker.folderName}`) ||
    `name:${marker.name}`;

  const existing = activeMarkerPopups.get(popupKey);
  if (existing) {
    // 已開過 — 直接 return 既有 popup，不重複建立
    return existing;
  }
  
  // 創建 popup 內容
  //
  // box-sizing / max-width / max-height 一齊保證 popup 整塊唔會超出
  // viewport，內容過長時自動可捲。video iframe 由內層自己用 aspect-ratio
  // 撐住，呢度 popupContent 唔再卡死寬度，等 Mapbox maxWidth 同 viewport
  // 細小情況下能自然縮。
  const popupContent = document.createElement('div');
  popupContent.style.cssText = `
    color: #44d3de;
    padding: 8px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background: rgba(0, 0, 0, 0.95);
    border-radius: 4px;
    box-sizing: border-box;
    max-width: 100%;
    /* 65vh × max zoom-scale (1.5) ≈ 97vh → 視覺最高仍在 viewport 內。
       超過就 overflow-y 出 scrollbar，使用者仍可看到所有內容。 */
    max-height: min(65vh, 480px);
    overflow-y: auto;
    overflow-x: hidden;
  `;
  
  // 標題
  // padding-right 24px 預留位俾 .mapboxgl-popup-close-button (位置 top:2px
  // right:2px，寬約 20px)，否則 marker name + folder badge 會被 close 按鈕
  // 蓋住右半邊。
  const title = document.createElement('div');
  title.textContent = marker.name;
  title.style.cssText = `
    margin: 0 0 8px 0;
    padding-right: 24px;
    color: #44d3de;
    font-size: 12px;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 6px;
  `;
  
  // Folder badge
  const folderBadge = document.createElement('span');
  folderBadge.textContent = marker.folderName || 'No Folder';
  folderBadge.style.cssText = `
    font-size: 9px;
    padding: 2px 8px;
    background: ${marker.color};
    color: #fff;
    border-radius: 8px;
    font-weight: normal;
  `;
  title.appendChild(folderBadge);
  popupContent.appendChild(title);
  
  // 根據 markerType 顯示不同內容
  const markerType = marker.markerType || 'video';
  
  if (markerType === 'video' && marker.videoUrl) {
    // Video Marker: responsive container + 真正能 stream 嘅 player
    //
    // 之前直接 iframe.src = videoUrl，對 .m3u8 / .flv 等 stream URL 瀏覽
    // 器會當 file 處理 (Chrome 唔識 HLS、所有現代瀏覽器都唔識 FLV) →
    // 變成下載而唔係播放。
    //
    // 而家根據 marker.videoType 揀 player：
    //   - hls (.m3u8)：Safari 行 native (canPlayType 'application/vnd.
    //     apple.mpegurl')；其他用 hls.js (lazy import，第一次開 popup
    //     先 load chunk，省主 bundle ~150KB gzip)
    //   - flv  (.flv)：flv.js (mse-flv-player) lazy import；live streaming
    //     直接 attach 到 video tag
    //   - mp4  (.mp4)：HTML5 native，<video src=>
    //   - 其他 / 未知：fallback 返 iframe (向後兼容 legacy 的 youtube /
    //     vimeo embed URL)
    //
    // popup 關閉時 cleanup —— player.destroy() 釋放 segment 下載迴圈、
    // 唔會喺 background 繼續食 bandwidth。
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      position: relative;
      width: min(360px, 70vw);
      max-width: 100%;
      aspect-ratio: 16 / 9;
      margin-bottom: 8px;
      border: 1px solid #44d3de;
      border-radius: 4px;
      overflow: hidden;
      background: #000;
    `;
    popupContent.appendChild(wrapper);

    const cleanupPlayer = attachVideoPlayer(
      wrapper,
      marker.videoUrl,
      (marker.videoType || 'hls').toLowerCase()
    );
    // 把 cleanup 暫存喺 popup instance，下面 popup.on('close') 會 call
    (popupContent as any)._videoCleanup = cleanupPlayer;
  } else if (markerType === 'device' && marker.deviceData && marker.deviceData.length > 0) {
    // Device Marker: 顯示 IoT 資料表格
    const deviceContainer = document.createElement('div');
    deviceContainer.style.cssText = `
      background: rgba(68, 211, 222, 0.1);
      border: 1px solid #44d3de;
      border-radius: 4px;
      padding: 10px;
      margin-bottom: 8px;
      min-width: 300px;
    `;
    
    marker.deviceData.forEach((data: {key: string, value: string}) => {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex;
        justify-content: space-between;
        padding: 6px 0;
        border-bottom: 1px solid rgba(68, 211, 222, 0.2);
        font-size: 12px;
      `;
      row.innerHTML = `
        <span style="color: #44d3de; font-weight: bold;">${data.key}:</span>
        <span style="color: #fff;">${data.value}</span>
      `;
      deviceContainer.appendChild(row);
    });
    
    popupContent.appendChild(deviceContainer);
  } else if (markerType === 'information') {
    // Information Marker: 顯示文字區塊 (即使沒有 infoText 也顯示 popup)
    const infoBlock = document.createElement('div');
    infoBlock.style.cssText = `
      background: rgba(68, 211, 222, 0.1);
      border: 1px solid #44d3de;
      border-radius: 4px;
      padding: 12px;
      margin-bottom: 8px;
      color: #fff;
      font-size: 12px;
      line-height: 1.6;
      white-space: pre-wrap;
      max-width: 400px;
    `;
    infoBlock.textContent = marker.infoText || 'No information available';
    popupContent.appendChild(infoBlock);
  } else if (markerType === 'location') {
    // Location Marker: 顯示基本位置資訊
    const locationInfo = document.createElement('div');
    locationInfo.style.cssText = `
      background: rgba(68, 211, 222, 0.1);
      border: 1px solid #44d3de;
      border-radius: 4px;
      padding: 12px;
      margin-bottom: 8px;
      color: #fff;
      font-size: 12px;
    `;
    locationInfo.textContent = 'Location marker';
    popupContent.appendChild(locationInfo);
  }
  
  // 位置資訊
  const positionInfo = document.createElement('div');
  positionInfo.style.cssText = 'font-size: 9px; color: rgba(68, 211, 222, 0.7); line-height: 1.4;';
  positionInfo.innerHTML = `
    <div>📍 ${marker.position.lng.toFixed(4)}, ${marker.position.lat.toFixed(4)} (${marker.position.altitude || 0}m)</div>
  `;
  popupContent.appendChild(positionInfo);
  
  // popup 用 Mapbox 原生 altitude (v3.11+) — popup 真正錨在
  // (lng, lat, altitude) 3D 點，不用手動算 pixel offset 也不用 move
  // listener。
  //
  // 不再強制 anchor: 'bottom'。
  // 之前強制「popup 永遠開喺 marker 上方」，但 video popup 高度
  // ~280px，當 marker 接近 viewport 頂（特別是 altitude flyTo 把 marker
  // 推到 viewport 偏上之後）popup 就被 viewport 上邊切走。讓 Mapbox 自己
  // 用 `_getAnchor()` 按可用空間挑（bottom / top / left / right / corner），
  // popup 總有足夠空間完全展開、唔會被擋。
  // pitchAlignment / rotationAlignment 並非 popup 的官方 option (見
  // mapbox-gl-js v3.17 popup.ts)，傳入會被忽略 — 移除以免誤導。
  // offset 用 anchor-aware object：bottom 錨點 → popup 在 marker 上方
  // 30px；top 錨點 → popup 在下方 30px；左右 / 角同理，全部維持 ~30px
  // 與 icon 的 neon glow 不重疊。
  const altitudeMeters = marker.position?.altitude || 0;

  const popup = new mapboxgl.Popup({
    closeButton: true,
    closeOnClick: false,
    // 細 viewport 預留 40px 邊距 (左右各 20)，PC 仍封頂 420px。
    maxWidth: 'min(420px, calc(100vw - 40px))',
    offset: {
      'top': [0, 30],
      'top-left': [20, 20],
      'top-right': [-20, 20],
      'bottom': [0, -30],
      'bottom-left': [20, -20],
      'bottom-right': [-20, -20],
      'left': [30, 0],
      'right': [-30, 0],
      'center': [0, 0],
    },
    altitude: altitudeMeters,
    className: 'marker-popup-dark',
  } as any)
    .setLngLat([marker.position.lng, marker.position.lat])
    .setDOMContent(popupContent)
    .addTo(map);

  // 記住此 marker 的 popup，下次同一個 marker 再 click 就直接 return 它，
  // 不會重複建立。popup close (使用者按 x 或程式 remove) 自動清掉登記。
  activeMarkerPopups.set(popupKey, popup);
  popup.on('close', () => {
    if (activeMarkerPopups.get(popupKey) === popup) {
      activeMarkerPopups.delete(popupKey);
    }
    // 釋放 hls.js / flv.js player：唔 destroy 嘅話 segment loader 會
    // 繼續喺 background 食 network、亦會 leak memory（特別係 live
    // stream 永遠唔會自然 end）。
    try {
      const cleanup = (popupContent as any)._videoCleanup as (() => void) | undefined;
      if (typeof cleanup === 'function') cleanup();
    } catch (err) {
      console.warn('[marker-video] cleanup failed', err);
    }
  });

  // zoom-aware scaling: 開 popup 那刻的 zoom 做 scale=1 reference，
  // 之後 zoom in/out popup 會跟住 map 內容一齊放大縮細，視覺上「黐住」
  // 3D 場景而不是浮在 viewport 上面。
  (popup as any)._scaleReferenceZoom = map.getZoom();
  ensurePopupScaleWired();
  // 立即套用一次，避免第一個 frame 出現原 size 之後才縮放的閃動
  applyPopupScale(popup, map.getZoom());

  return popup;
}

// ========================================
// WebSocket 實時控制功能
// ========================================

const WS_URL = 'wss://wss2.projectsndev.com';
let ws: WebSocket | null = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 3000;

// 儲存創建的 cubes、beams 和 labels
const cubeMap = new Map<string, THREE.Mesh>();
const beamMap = new Map<string, THREE.Mesh>();
const labelMap = new Map<string, THREE.Mesh>();

/**
 * 獲取指定 element 的中心位置（世界座標）
 * 使用 model.getItemsGeometry() 方法（參考 model_information_v1）
 */
async function getElementWorldPosition(modelId: string, localId: number): Promise<THREE.Vector3 | null> {
  try {
    const modelInfo = modelManager?.getModelInfo(modelId);
    if (!modelInfo || modelInfo.modelType !== 'frag') {
      return null;
    }
    
    const fragmentModel = modelInfo.model as FRAGS.FragmentsModel;
    const geometries = await fragmentModel.getItemsGeometry([localId]);
    
    if (!geometries || geometries.length === 0) {
      return null;
    }
    
    // 計算所有 geometry 的 bounding box（參考 model_information_v1 第 937-948 行）
    const box = new THREE.Box3();
    
    for (const meshDataArray of geometries) {
      for (const meshData of meshDataArray) {
        const { positions, transform } = meshData;
        
        if (!positions) continue;
        
        // 遍歷所有頂點並應用 transform
        for (let i = 0; i < positions.length; i += 3) {
          const vertex = new THREE.Vector3(
            positions[i],
            positions[i + 1],
            positions[i + 2]
          );
          
          // 應用 transform matrix（參考 model_information_v1 第 946 行）
          if (transform) {
            vertex.applyMatrix4(transform);
          }
          
          box.expandByPoint(vertex);
        }
      }
    }
    
    if (box.isEmpty()) {
      console.warn(`⚠️ Bounding box is empty for localId ${localId}`);
      return null;
    }
    
    // 獲取 bounding box 的中心
    const center = new THREE.Vector3();
    box.getCenter(center);
    
    // ⚠️ 關鍵：model.getItemsGeometry() 返回的座標已經應用了 geometry 的 transform
    // 但還需要加上 model.object.position 的偏移（因為模型本身可能有位置偏移）
    const modelObject = fragmentModel.object;
    center.add(modelObject.position);
    
    return center;
  } catch (error) {
    console.warn(`⚠️ Error getting element position for ${modelId}-${localId}:`, error);
    return null;
  }
}

function sendIdentify() {
  const userId = getCurrentUserId();
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({
      type: 'identify',
      clientType: 'index-page',
      userId: userId
    }));
  }
}

function sendRegisterModels() {
  const userId = getCurrentUserId();
  // 沒 userId 不送（中央 WSS 會 reject 並 spam log；__echovReidentifyWS 之後會補送）
  if (!userId) return;
  const loadedModelIds = Array.from(modelManager?.getAllModels().keys() || []);
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({
      type: 'registerModels',
      modelIds: loadedModelIds,
      userId: userId
    }));
  }
}

function connectWebSocket() {
  try {
    ws = new WebSocket(WS_URL);
    
    ws.onopen = () => {
      reconnectAttempts = 0;
      sendIdentify();
      // 等 modelManager 準備好再 register
      setTimeout(() => sendRegisterModels(), 1000);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      } catch (error) {
        console.warn('⚠️ [WebSocket] Parse error:', error);
      }
    };
    
    ws.onclose = () => {
      ws = null;
      attemptReconnect();
    };
    
    ws.onerror = (error) => {
      console.warn('⚠️ [WebSocket] Error:', error);
    };
  } catch (error) {
    console.warn('⚠️ [WebSocket] Connection failed:', error);
    attemptReconnect();
  }
}

function attemptReconnect() {
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.warn('⚠️ Max reconnection attempts reached');
    return;
  }
  
  reconnectAttempts++;
  setTimeout(() => {
    connectWebSocket();
  }, RECONNECT_DELAY);
}

async function handleWebSocketMessage(data: any) {
  const { type } = data;
  
  switch (type) {
    case 'welcome':
      break;
    case 'createCubeAtEstimatedLocationRequest':
    case 'addCube':
      await handleAddCube(data);
      break;
    case 'updateCubeAtLocationRequest':
    case 'updateCubeByLocalId':
      await handleUpdateCube(data);
      break;
    case 'deleteCubeByLocalId':
      handleDeleteCube(data);
      break;
    case 'deleteAllCubes':
      handleDeleteAllCubes();
      break;
    case 'createLightBeamRequest':
      await handleCreateLightBeam(data);
      break;
    case 'modelTransformUpdate':
      await handleModelTransformUpdate(data);
      break;
    case 'modelCreated':
      await handleModelCreated(data);
      break;
    case 'modelDeleted':
      await handleModelDeleted(data);
      break;
    case 'updateLightBeamRequest':
      await handleUpdateLightBeam(data);
      break;
    case 'removeLightBeamRequest':
      handleDeleteLightBeam(data);
      break;
    case 'setColor':
      await handleSetColor(data);
      break;
    case 'resetColor':
      await handleResetColor(data);
      break;
    case 'createLabelRequest':
      await handleCreateLabel(data);
      break;
    case 'updateLabelRequest':
      await handleUpdateLabel(data);
      break;
    case 'deleteLabelRequest':
      handleDeleteLabel(data);
      break;
    case 'registerModels':
      break;
    default:
      console.warn('⚠️ Unknown message type:', type);
  }
}

async function handleAddCube(data: any) {
  const { modelId, localId, localIds, offset, size, color, rotation } = data;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  const idsToProcess = localIds || (localId !== undefined ? [localId] : []);
  if (idsToProcess.length === 0) {
    console.warn('⚠️ No localIds to process');
    return;
  }
  
  for (const id of idsToProcess) {
    const key = `${modelId}-${id}`;
    
    // 移除舊的 cube
    if (cubeMap.has(key)) {
      const oldCube = cubeMap.get(key)!;
      scene.remove(oldCube);
      oldCube.geometry.dispose();
      (oldCube.material as THREE.Material).dispose();
      cubeMap.delete(key);
    }
    
    // 獲取 element 的實際位置
    const elementPosition = await getElementWorldPosition(modelId, id);
    if (!elementPosition) {
      console.warn(`⚠️ Cannot get position for ${key}, skipping`);
      continue;
    }
  
    
    // 創建 cube
    const cubeSize = size || 1;
    const geometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
    const material = new THREE.MeshBasicMaterial({ 
      color: color || '#FF0000',
      transparent: true,
      opacity: 0.8
    });
    const cube = new THREE.Mesh(geometry, material);
    
    // 設置位置（element 位置 + offset）
    const finalOffset = offset || { x: 0, y: 0, z: 0 };
    cube.position.set(
      elementPosition.x + (finalOffset.x || 0),
      elementPosition.y + (finalOffset.y || 0),
      elementPosition.z + (finalOffset.z || 0)
    );
    
    // 設置旋轉
    if (rotation) {
      cube.rotation.set(
        rotation.x || 0,
        rotation.y || 0,
        rotation.z || 0
      );
    }
    
    // 在 cube 上記錄它屬於哪個 model
    (cube as any).userData = { modelId, localId: id, key };
    
    // ⚠️ 關鍵修正：將 cube 加到對應的 model object 下，而不是 scene
    const cubeModelInfo = modelManager?.getModelInfo(modelId);
    if (cubeModelInfo && cubeModelInfo.modelType === 'frag') {
      const fragmentModel = cubeModelInfo.model as FRAGS.FragmentsModel;
      const modelObject = fragmentModel.object;
      
      // 將世界座標轉換為 model 的本地座標
      const worldPos = cube.position.clone();
      const localPos = worldPos.sub(modelObject.position);
      cube.position.copy(localPos);
      
      // 加到 model 的 object 下
      modelObject.add(cube);
    } else {
      // 如果找不到 model，還是加到 scene（fallback）
      scene.add(cube);
    }
    
    cubeMap.set(key, cube);
  }
}

// ========================================
// 動畫系統
// ========================================

interface AnimationTarget {
  modelId: string;
  startTransform: {
    translateX: number;
    translateY: number;
    translateZ: number;
    rotateX: number;
    rotateY: number;
    rotateZ: number;
    scale: number;
  };
  endTransform: {
    translateX: number;
    translateY: number;
    translateZ: number;
    rotateX: number;
    rotateY: number;
    rotateZ: number;
    scale: number;
  };
  startTime: number;
  duration: number;
}

const activeAnimations = new Map<string, AnimationTarget>();

// 檢測是否為手機裝置（安全檢查）
let isMobileDevice = false;
try {
  isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
} catch (e) {
  isMobileDevice = false;
}

// 動畫配置（統一設定）
const ANIMATION_CONFIG = {
  DURATION: 800, // 統一動畫時長
  ENABLED: true  // 是否啟用動畫
};

// 手機效能優化配置（已禁用，使用 PC view）
const MOBILE_PERFORMANCE_CONFIG = {
  MAX_VISIBLE_MODELS: 50, // 統一設定
  REDUCE_QUALITY: false, // 禁用品質降低
  DISABLE_SHADOWS: false, // 禁用陰影關閉
  LOWER_FRAGMENT_PRECISION: false // 禁用精度降低
};

/**
 * 緩動函數：easeInOutCubic
 */
function easeInOutCubic(t: number): number {
  return t < 0.5
    ? 4 * t * t * t
    : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * 啟動模型變換動畫
 */
function animateModelTransform(
  modelId: string,
  targetPosition?: { x: number; y: number; z: number },
  targetRotation?: { x: number; y: number; z: number },
  targetScale?: number
) {
  if (!modelManager) return;
  
  const modelInfo = modelManager.getModelInfo(modelId);
  if (!modelInfo) return;
  
  // 如果動畫被禁用，直接更新
  if (!ANIMATION_CONFIG.ENABLED) {
    if (targetPosition) {
      modelInfo.transform.translateX = targetPosition.x;
      modelInfo.transform.translateY = targetPosition.y;
      modelInfo.transform.translateZ = targetPosition.z;
    }
    if (targetRotation) {
      modelInfo.transform.rotateX = targetRotation.x;
      modelInfo.transform.rotateY = targetRotation.y;
      modelInfo.transform.rotateZ = targetRotation.z;
    }
    if (targetScale !== undefined) {
      modelInfo.transform.scale = targetScale;
    }
    map.triggerRepaint();
    return;
  }
  
  // 取消現有動畫
  if (activeAnimations.has(modelId)) {
    activeAnimations.delete(modelId);
  }
  
  // 創建動畫目標
  const animation: AnimationTarget = {
    modelId,
    startTransform: {
      translateX: modelInfo.transform.translateX,
      translateY: modelInfo.transform.translateY,
      translateZ: modelInfo.transform.translateZ,
      rotateX: modelInfo.transform.rotateX,
      rotateY: modelInfo.transform.rotateY,
      rotateZ: modelInfo.transform.rotateZ,
      scale: modelInfo.transform.scale
    },
    endTransform: {
      translateX: targetPosition?.x ?? modelInfo.transform.translateX,
      translateY: targetPosition?.y ?? modelInfo.transform.translateY,
      translateZ: targetPosition?.z ?? modelInfo.transform.translateZ,
      rotateX: targetRotation?.x ?? modelInfo.transform.rotateX,
      rotateY: targetRotation?.y ?? modelInfo.transform.rotateY,
      rotateZ: targetRotation?.z ?? modelInfo.transform.rotateZ,
      scale: targetScale ?? modelInfo.transform.scale
    },
    startTime: (performance && performance.now) ? performance.now() : Date.now(),
    duration: ANIMATION_CONFIG.DURATION
  };
  
  activeAnimations.set(modelId, animation);
}

/**
 * 更新所有活動動畫
 */
function updateAnimations() {
  if (activeAnimations.size === 0) return;
  
  const now = (performance && performance.now) ? performance.now() : Date.now();
  const completedAnimations: string[] = [];
  const modelsToUpdateMarkers = new Set<string>();
  
  activeAnimations.forEach((animation, modelId) => {
    const modelInfo = modelManager?.getModelInfo(modelId);
    if (!modelInfo) {
      completedAnimations.push(modelId);
      return;
    }
    
    const elapsed = now - animation.startTime;
    const progress = Math.min(elapsed / animation.duration, 1);
    const easedProgress = easeInOutCubic(progress);
    
    // 插值計算
    const start = animation.startTransform;
    const end = animation.endTransform;
    
    modelInfo.transform.translateX = start.translateX + (end.translateX - start.translateX) * easedProgress;
    modelInfo.transform.translateY = start.translateY + (end.translateY - start.translateY) * easedProgress;
    modelInfo.transform.translateZ = start.translateZ + (end.translateZ - start.translateZ) * easedProgress;
    modelInfo.transform.rotateX = start.rotateX + (end.rotateX - start.rotateX) * easedProgress;
    modelInfo.transform.rotateY = start.rotateY + (end.rotateY - start.rotateY) * easedProgress;
    modelInfo.transform.rotateZ = start.rotateZ + (end.rotateZ - start.rotateZ) * easedProgress;
    modelInfo.transform.scale = start.scale + (end.scale - start.scale) * easedProgress;
    
    // 如果位置有變化，標記需要更新 marker
    if (start.translateX !== end.translateX || start.translateY !== end.translateY || start.translateZ !== end.translateZ) {
      modelsToUpdateMarkers.add(modelId);
    }
    
    // 動畫完成
    if (progress >= 1) {
      completedAnimations.push(modelId);
    }
  });
  
  // 更新 folder markers（對於正在動畫的模型）
  if (modelsToUpdateMarkers.size > 0) {
    updateFolderMarkersForModels(modelsToUpdateMarkers);
  }
  
  // 清除完成的動畫
  completedAnimations.forEach(id => activeAnimations.delete(id));
  
  // 觸發重繪
  if (activeAnimations.size > 0 || completedAnimations.length > 0) {
    map.triggerRepaint();
  }
}

/**
 * 更新指定模型的 folder markers
 */
function updateFolderMarkersForModels(modelIds: Set<string>) {
  if (!modelManager) return;
  
  const folderMarkers = modelManager.getFolderMarkers();
  
  modelIds.forEach(modelId => {
    const modelInfo = modelManager.getModelInfo(modelId);
    if (!modelInfo || !(modelInfo.config as any).folder) return;
    
    const folderId = (modelInfo.config as any).folder;
    const folderName = folderCache.get(folderId)?.name || folderId;
    
    // 檢查這個模型是否是 folder 中的第一個
    const allModels = Array.from(modelManager.getAllModels().entries());
    const modelsInFolder = allModels
      .filter(([_, info]) => (info.config as any).folder === folderId)
      .sort((a, b) => a[0].localeCompare(b[0]));
    
    if (modelsInFolder.length > 0 && modelsInFolder[0][0] === modelId) {
      const folderMarkerInfo = folderMarkers.get(folderName);
      if (folderMarkerInfo) {
        const newLngLat: [number, number] = [
          modelInfo.config.position.lng,
          modelInfo.config.position.lat
        ];
        folderMarkerInfo.marker.setLngLat(newLngLat);
        folderMarkerInfo.point.setLngLat(newLngLat);
      }
    }
  });
}

// ========================================
// WebSocket 處理函數
// ========================================

async function handleModelTransformUpdate(data: any) {
  const { modelId, updates } = data;
  
  if (!modelManager) return;
  
  const modelInfo = modelManager.getModelInfo(modelId);
  if (!modelInfo) return;
  
  // 診斷：log 每一次 transform mutation 來源（找出誰在改 transform 造成偏移）
  console.warn(`[FRAG-MUTATE] modelTransformUpdate ${modelId}`, {
    hasPosition: !!updates.position,
    hasRotation: !!updates.rotation,
    hasScale: updates.scale !== undefined,
    hasOffset: !!updates.offset,
    updates,
    sourceData: { from: data.userId, ts: data.timestamp }
  });
  
  // 清除 cache（數據已更新）
  const userId = getCurrentUserId();
  if (userId) {
    clearUserCache(userId);
  }
  
  // 準備動畫目標
  let targetPosition: { x: number; y: number; z: number } | undefined;
  let targetRotation: { x: number; y: number; z: number } | undefined;
  let targetScale: number | undefined;
  
  // 更新 position
  if (updates.position) {
    const { lng, lat, altitude } = updates.position;
    
    modelInfo.config.position.lng = lng;
    modelInfo.config.position.lat = lat;
    modelInfo.config.position.altitude = altitude || 0;
    
    const mercatorCoord = mapboxgl.MercatorCoordinate.fromLngLat(
      [lng, lat],
      altitude || 0
    );
    
    targetPosition = {
      x: mercatorCoord.x,
      y: mercatorCoord.y,
      z: mercatorCoord.z
    };
  }
  
  // 更新 rotation
  if (updates.rotation) {
    const { x, y, z } = updates.rotation;
    
    modelInfo.config.rotation.x = x;
    modelInfo.config.rotation.y = y;
    modelInfo.config.rotation.z = z;
    
    targetRotation = { x, y, z };
  }
  
  // 更新 scale
  if (updates.scale !== undefined) {
    modelInfo.config.scale = updates.scale;
    
    const mercatorCoord = mapboxgl.MercatorCoordinate.fromLngLat(
      [modelInfo.config.position.lng, modelInfo.config.position.lat],
      modelInfo.config.position.altitude || 0
    );
    
    targetScale = mercatorCoord.meterInMercatorCoordinateUnits() * updates.scale;
  }
  
  // 更新 offset（實時應用）
  if (updates.offset) {
    const { x, y, z } = updates.offset;
    const oldOffset = modelInfo.config.offset || { x: 0, y: 0, z: 0 };
    modelInfo.config.offset = { x, y, z };
    
    // 立即應用 offset 到模型位置
    if (modelInfo.modelType === 'frag') {
      // Frag 模型：更新 object.position
      const fragModel = modelInfo.model as any;
      if (fragModel.object) {
        // 計算 offset 的變化量
        const deltaX = (x || 0) - (oldOffset.x || 0);
        const deltaY = (y || 0) - (oldOffset.y || 0);
        const deltaZ = (z || 0) - (oldOffset.z || 0);
        
        // 應用變化量
        fragModel.object.position.x += deltaX;
        fragModel.object.position.y += deltaY;
        fragModel.object.position.z += deltaZ;
        
        console.log(`✅ Applied offset to frag model ${modelId}:`, { x, y, z });
      }
    } else if (modelInfo.modelType === 'gltf') {
      // glTF 模型：更新 Group.position
      const gltfModel = modelInfo.model as THREE.Group;
      if (gltfModel) {
        // 計算 offset 的變化量
        const deltaX = (x || 0) - (oldOffset.x || 0);
        const deltaY = (y || 0) - (oldOffset.y || 0);
        const deltaZ = (z || 0) - (oldOffset.z || 0);
        
        // 應用變化量
        gltfModel.position.x += deltaX;
        gltfModel.position.y += deltaY;
        gltfModel.position.z += deltaZ;
        
        console.log(`✅ Applied offset to gltf model ${modelId}:`, { x, y, z });
      }
    }
    
    // 觸發重繪
    map.triggerRepaint();
  }
  
  // 啟動動畫
  if (targetPosition || targetRotation || targetScale !== undefined) {
    animateModelTransform(modelId, targetPosition, targetRotation, targetScale);
  }
  
  // 如果這個 model 有 folder，且是 folder 中的第一個 model，更新 folder marker
  if (updates.position && modelInfo.config.folder) {
    const allModels = Array.from(modelManager.getAllModels().entries());
    const modelsInFolder = allModels
      .filter(([_, info]) => (info.config as any).folder === modelInfo.config.folder)
      .sort((a, b) => a[0].localeCompare(b[0]));
    
    if (modelsInFolder.length > 0 && modelsInFolder[0][0] === modelId) {
      const folderMarkers = modelManager.getFolderMarkers();
      const folderName = (modelInfo.config as any).folderName || modelInfo.config.folder;
      const folderMarkerInfo = folderMarkers.get(folderName);
      
      const { lng, lat } = updates.position;
      
      if (folderMarkerInfo) {
        // 更新已註冊的 folder markers
        if (folderMarkerInfo.marker) {
          folderMarkerInfo.marker.setLngLat([lng, lat]);
        }
        if (folderMarkerInfo.point) {
          folderMarkerInfo.point.setLngLat([lng, lat]);
        }
      } else {
        // 如果在 folderMarkers 中找不到，嘗試直接從 DOM 中查找並更新
        // 這些可能是在 loadAndDisplayModelMarkers() 中創建的早期 markers
        const allMarkers = document.querySelectorAll('.model-marker[data-folder-name], .model-point[data-folder-name]');
        allMarkers.forEach((markerEl) => {
          const markerFolderName = markerEl.getAttribute('data-folder-name');
          if (markerFolderName === folderName) {
            // 找到對應的 Mapbox marker 實例並更新位置
            // 注意：這裡我們無法直接訪問 Mapbox marker 實例，所以需要移除舊的並創建新的
            // 或者我們可以選擇不處理，等待下次完整重載
          }
        });
      }
    }
  }
  
  map.triggerRepaint();
}

async function handleModelCreated(data: any) {
  const { model } = data;
  
  if (!modelManager) return;
  
  // 清除 cache（新模型已創建）
  const userId = getCurrentUserId();
  if (userId) {
    clearUserCache(userId);
  }
  
  const existingModel = modelManager.getModelInfo(model.id);
  if (existingModel) return;
  
  // 構建 ModelConfig
  const modelConfig: any = {
    id: model.id,
    name: model.name,
    url: model.url || '',
    fileId: model.fileId,
    folder: model.folder,
    position: model.position,
    rotation: model.rotation,
    scale: model.scale || 1,
    offset: model.offset || { x: 0, y: 0, z: 0 },
    color: model.color || '#ffffff',
    visible: model.visible !== false,
    modelType: model.modelType
  };
  
  try {
    await modelManager.loadModel(modelConfig);
    
    // 如果模型有 folder，檢查是否需要創建/更新 folder marker
    if (model.folder && map) {
      const allModels = Array.from(modelManager.getAllModels().entries());
      const modelsInFolder = allModels
        .filter(([_, info]) => (info.config as any).folder === model.folder)
        .sort((a, b) => a[0].localeCompare(b[0]));
      
      const folderMarkers = modelManager.getFolderMarkers();
      const folderName = (modelConfig as any).folderName || modelConfig.folder;
      const existingMarker = folderMarkers.get(folderName);
      
      if (!existingMarker && modelsInFolder.length > 0 && modelsInFolder[0][0] === model.id) {
        
        // 創建 point marker（彩色圓點）
        const pointEl = document.createElement('div');
        pointEl.className = 'model-point';
        pointEl.style.backgroundColor = modelConfig.color || '#ff0000';
        pointEl.title = modelConfig.name;
        pointEl.addEventListener('click', () => {
          modelManager.flyToModel(modelConfig.id);
        });
        const point = new mapboxgl.Marker(pointEl)
          .setLngLat([modelConfig.position.lng, modelConfig.position.lat])
          .addTo(map);
        
        // 創建 text marker（文字標籤）
        const markerEl = document.createElement('div');
        markerEl.className = 'model-marker';
        const folderDiv = document.createElement('div');
        folderDiv.className = 'marker-folder';
        folderDiv.textContent = folderName;
        const statusDiv = document.createElement('div');
        statusDiv.className = 'marker-status';
        statusDiv.textContent = 'Normal';
        statusDiv.setAttribute('data-folder-id', folderName);
        markerEl.appendChild(folderDiv);
        markerEl.appendChild(statusDiv);
        markerEl.addEventListener('click', () => {
          // 創建 markerData 並顯示 popup
          const markerData = {
            name: folderName,
            folderName: folderName,
            position: modelConfig.position,
            color: modelConfig.color,
            markerType: 'model'
          };
          showMarkerPopup(markerData);
        });
        const marker = new mapboxgl.Marker(markerEl, { offset: [0, -30] })
          .setLngLat([modelConfig.position.lng, modelConfig.position.lat])
          .addTo(map);
        
        folderMarkers.set(folderName, { marker, point, models: modelsInFolder.map(([_, info]) => info.config) });
      }
    }
    
    map.triggerRepaint();
  } catch (error) {
    console.error(`❌ Failed to load model ${model.id}:`, error);
  }
}

/**
 * 收到 `modelDeleted` WS 廣播時，從場景移除模型 + 連帶清理 cube/beam/label/marker。
 * 這是 §10 sync 的一部分：其他 user/tab 透過 DELETE /api/models/:id 刪了 model，
 * 本地畫面要即時跟住更新。
 */
async function handleModelDeleted(data: any) {
  const { modelId, fileId } = data;
  if (!modelManager) return;

  const modelInfo = modelManager.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }

  console.log(`🗑️ [WS] handleModelDeleted: ${modelId}`);

  // 1. 清掉 cube / beam / label（這些是 attach 到 model.object 的）
  const cubeKeysToRemove: string[] = [];
  cubeMap.forEach((mesh, key) => {
    if (key.startsWith(`${modelId}-`)) {
      if (mesh.parent) mesh.parent.remove(mesh);
      try { mesh.geometry?.dispose(); } catch { /* noop */ }
      try { (mesh.material as THREE.Material)?.dispose(); } catch { /* noop */ }
      cubeKeysToRemove.push(key);
    }
  });
  cubeKeysToRemove.forEach((k) => cubeMap.delete(k));

  const beamKeysToRemove: string[] = [];
  beamMap.forEach((mesh, key) => {
    if (key.startsWith(`${modelId}-`)) {
      if (mesh.parent) mesh.parent.remove(mesh);
      try { mesh.geometry?.dispose(); } catch { /* noop */ }
      try { (mesh.material as THREE.Material)?.dispose(); } catch { /* noop */ }
      beamKeysToRemove.push(key);
    }
  });
  beamKeysToRemove.forEach((k) => beamMap.delete(k));

  const labelKeysToRemove: string[] = [];
  labelMap.forEach((mesh, key) => {
    if (key.startsWith(`${modelId}-`)) {
      if (mesh.parent) mesh.parent.remove(mesh);
      try { mesh.geometry?.dispose(); } catch { /* noop */ }
      try {
        const map = (mesh.material as any)?.map;
        if (map) map.dispose();
      } catch { /* noop */ }
      try { (mesh.material as THREE.Material)?.dispose(); } catch { /* noop */ }
      labelKeysToRemove.push(key);
    }
  });
  labelKeysToRemove.forEach((k) => labelMap.delete(k));

  // 2. 從 ModelManager 中移除（從 scene + fragments + 內部 map 清掉）
  try {
    modelManager.removeModel(modelId);
  } catch (err) {
    console.warn(`⚠️ removeModel failed for ${modelId}:`, err);
  }

  // 3. 同 user 的其他 tab 也要清 IndexedDB cache，避免之後 stale buffer
  const userId = getCurrentUserId();
  if (userId) {
    clearUserCache(userId);
  }
  if (fileId) {
    console.log(`   🧹 Cleared user model-buffer cache (fileId=${fileId})`);
  }

  // 4. 如果這個 model 是 folder 中最後一個，順手清掉 folder marker
  const folderName =
    (modelInfo.config as any)?.folderName || (modelInfo.config as any)?.folder;
  if (folderName) {
    const stillInFolder = Array.from(modelManager.getAllModels().entries()).some(
      ([, info]) => {
        const cfg: any = info.config;
        return (cfg?.folderName || cfg?.folder) === folderName;
      }
    );
    if (!stillInFolder) {
      const folderMarkers = modelManager.getFolderMarkers();
      const folderMarkerInfo = folderMarkers.get(folderName);
      if (folderMarkerInfo) {
        try { folderMarkerInfo.marker?.remove(); } catch { /* noop */ }
        try { folderMarkerInfo.point?.remove(); } catch { /* noop */ }
        folderMarkers.delete(folderName);
        console.log(`   🗺️  Removed folder marker: ${folderName}`);
      }
    }
  }

  if (map) map.triggerRepaint();
}

async function handleUpdateCube(data: any) {
  const { modelId, localId, updates } = data;
  const key = `${modelId}-${localId}`;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  const cube = cubeMap.get(key);
  if (!cube) {
    console.warn(`⚠️ Cube not found: ${key}`);
    return;
  }
  
  // 更新顏色
  if (updates.color) {
    (cube.material as THREE.MeshBasicMaterial).color.set(updates.color);
  }
  
  // 更新大小
  if (updates.size) {
    cube.scale.setScalar(updates.size / 1);
  }
  
  // 更新位置（如果有 offset）
  if (updates.offset) {
    const elementPosition = await getElementWorldPosition(modelId, localId);
    if (elementPosition) {
      let targetPos = new THREE.Vector3(
        elementPosition.x + (updates.offset.x || 0),
        elementPosition.y + (updates.offset.y || 0),
        elementPosition.z + (updates.offset.z || 0)
      );
      
      // 如果 cube 在 model.object 下，轉換為本地座標
      if (cube.parent && cube.parent !== scene) {
        targetPos.sub(cube.parent.position);
      }
      
      cube.position.copy(targetPos);
    }
  }
  
  // 更新旋轉
  if (updates.rotation) {
    cube.rotation.set(
      updates.rotation.x || 0,
      updates.rotation.y || 0,
      updates.rotation.z || 0
    );
  }
}

function handleDeleteCube(data: any) {
  const { modelId, localId } = data;
  const key = `${modelId}-${localId}`;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  const cube = cubeMap.get(key);
  if (cube) {
    // 從 parent 移除（可能是 scene 或 model.object）
    if (cube.parent) {
      cube.parent.remove(cube);
    }
    cube.geometry.dispose();
    (cube.material as THREE.Material).dispose();
    cubeMap.delete(key);
  }
}

function handleDeleteAllCubes() {
  cubeMap.forEach((cube) => {
    // 從 parent 移除（可能是 scene 或 model.object）
    if (cube.parent) {
      cube.parent.remove(cube);
    }
    cube.geometry.dispose();
    (cube.material as THREE.Material).dispose();
  });
  cubeMap.clear();
}

async function handleCreateLightBeam(data: any) {
  const { modelId, localId, height, color, status, scale, offset } = data;
  const key = `${modelId}-${localId}`;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  // 移除舊的 beam
  if (beamMap.has(key)) {
    const oldBeam = beamMap.get(key)!;
    // 從 parent 移除（可能是 scene 或 model.object）
    if (oldBeam.parent) {
      oldBeam.parent.remove(oldBeam);
    }
    oldBeam.geometry.dispose();
    (oldBeam.material as THREE.Material).dispose();
    beamMap.delete(key);
  }
  
  // 獲取 element 的實際位置
  const elementPosition = await getElementWorldPosition(modelId, localId);
  if (!elementPosition) {
    console.warn(`⚠️ Cannot get position for ${key}, skipping beam creation`);
    return;
  }
  
  // 創建光柱
  const beamHeight = height || 10;
  const beamRadius = (scale || 1) * 0.5;
  const geometry = new THREE.CylinderGeometry(beamRadius, beamRadius, beamHeight, 32);
  const material = new THREE.MeshBasicMaterial({
    color: color || '#00FF00',
    transparent: true,
    opacity: status === 'warning' ? 0.6 : 0.8
  });
  const beam = new THREE.Mesh(geometry, material);
  
  // 設置位置（element 位置 + offset + 光柱高度的一半）
  const finalOffset = offset || { x: 0, y: 0, z: 0 };
  beam.position.set(
    elementPosition.x + (finalOffset.x || 0),
    elementPosition.y + (finalOffset.y || 0) + beamHeight / 2,
    elementPosition.z + (finalOffset.z || 0)
  );
  
  // 在 beam 上記錄它屬於哪個 model（方便除錯）
  (beam as any).userData = { modelId, localId, key };
  
  // ⚠️ 關鍵修正：將 beam 加到對應的 model object 下，而不是 scene
  // 這樣 beam 的位置需要轉換為相對於 model 的本地座標
  if (modelInfo && modelInfo.modelType === 'frag') {
    const fragmentModel = modelInfo.model as FRAGS.FragmentsModel;
    const modelObject = fragmentModel.object;
    
    // 將世界座標轉換為 model 的本地座標
    const worldPos = beam.position.clone();
    const localPos = worldPos.sub(modelObject.position);
    beam.position.copy(localPos);
    
    // 加到 model 的 object 下
    modelObject.add(beam);
  } else {
    // 如果找不到 model，還是加到 scene（fallback）
    scene.add(beam);
  }
  
  beamMap.set(key, beam);
}

async function handleUpdateLightBeam(data: any) {
  const { modelId, localId, height, color, status, scale, offset } = data;
  const key = `${modelId}-${localId}`;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  const beam = beamMap.get(key);
  if (!beam) {
    console.warn(`⚠️ Light beam not found: ${key}, creating new one`);
    await handleCreateLightBeam(data);
    return;
  }
  
  // 更新顏色
  if (color !== undefined) {
    (beam.material as THREE.MeshBasicMaterial).color.set(color);
  }
  
  // 更新透明度（根據 status）
  if (status !== undefined) {
    (beam.material as THREE.MeshBasicMaterial).opacity = status === 'warning' ? 0.6 : 0.8;
  }
  
  // 獲取當前的 geometry 參數
  const currentGeometry = beam.geometry as THREE.CylinderGeometry;
  const currentHeight = currentGeometry.parameters.height;
  const currentRadius = currentGeometry.parameters.radiusTop;
  
  // 計算新的參數
  const newHeight = height !== undefined ? height : currentHeight;
  const newRadius = scale !== undefined ? (scale * 0.5) : currentRadius;
  
  // 如果高度或半徑改變，重新創建 geometry
  if (height !== undefined || scale !== undefined) {
    beam.geometry.dispose();
    beam.geometry = new THREE.CylinderGeometry(newRadius, newRadius, newHeight, 32);
    
    // 重新計算位置（因為高度可能改變了）
    const elementPosition = await getElementWorldPosition(modelId, localId);
    if (elementPosition) {
      const finalOffset = offset || { x: 0, y: 0, z: 0 };
      
      let targetPos = new THREE.Vector3(
        elementPosition.x + (finalOffset.x || 0),
        elementPosition.y + (finalOffset.y || 0) + newHeight / 2,
        elementPosition.z + (finalOffset.z || 0)
      );
      
      // 如果 beam 在 model.object 下，轉換為本地座標
      if (beam.parent && beam.parent !== scene) {
        targetPos.sub(beam.parent.position);
      }
      
      beam.position.copy(targetPos);
    }
  } else if (offset !== undefined) {
    // 只更新 offset
    const elementPosition = await getElementWorldPosition(modelId, localId);
    if (elementPosition) {
      let targetPos = new THREE.Vector3(
        elementPosition.x + (offset.x || 0),
        elementPosition.y + (offset.y || 0) + newHeight / 2,
        elementPosition.z + (offset.z || 0)
      );
      
      // 如果 beam 在 model.object 下，轉換為本地座標
      if (beam.parent && beam.parent !== scene) {
        targetPos.sub(beam.parent.position);
      }
      
      beam.position.copy(targetPos);
    }
  }
}

function handleDeleteLightBeam(data: any) {
  const { modelId, localId } = data;
  const key = `${modelId}-${localId}`;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  const beam = beamMap.get(key);
  if (beam) {
    // 從 parent 移除（可能是 scene 或 model.object）
    if (beam.parent) {
      beam.parent.remove(beam);
    }
    beam.geometry.dispose();
    (beam.material as THREE.Material).dispose();
    beamMap.delete(key);
  }
}

async function handleSetColor(data: any) {
  const { modelId, localIds, color, opacity } = data;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  if (modelInfo.modelType !== 'frag') {
    console.warn(`⚠️ Model ${modelId} is not a fragment model`);
    return;
  }
  
  try {
    const fragmentModel = modelInfo.model as FRAGS.FragmentsModel;
    
    // 使用 Fragments highlight API 設置顏色
    const materialDef: FRAGS.MaterialDefinition = {
      color: new THREE.Color(color),
      opacity: opacity !== undefined ? opacity : 1.0,
      transparent: opacity !== undefined && opacity < 1.0,
      renderedFaces: FRAGS.RenderedFaces.TWO
    };
    
    await fragmentModel.highlight(localIds, materialDef);
  } catch (error) {
    console.warn(`⚠️ Error setting color:`, error);
  }
}

async function handleResetColor(data: any) {
  const { modelId, localIds } = data;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  if (modelInfo.modelType !== 'frag') {
    console.warn(`⚠️ Model ${modelId} is not a fragment model`);
    return;
  }
  
  try {
    const fragmentModel = modelInfo.model as any;
    
    // 重置顏色（移除 highlight）
    await fragmentModel.resetHighlight(localIds);
  } catch (error) {
    console.warn(`⚠️ Error resetting color:`, error);
  }
}

async function handleCreateLabel(data: any) {
  const { modelId, localId, text, color, fontSize, offset, rotation } = data;
  const key = `${modelId}-${localId}`;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  // 檢查 model 類型
  if (modelInfo.modelType !== 'frag') {
    console.warn(`⚠️ Model ${modelId} is not a fragment model`);
    return;
  }
  
  const fragmentModel = modelInfo.model as FRAGS.FragmentsModel;
  const modelObject = fragmentModel.object;
  
  // 移除舊的 label
  if (labelMap.has(key)) {
    const oldLabel = labelMap.get(key)!;
    if (oldLabel.parent) {
      oldLabel.parent.remove(oldLabel);
    }
    if (oldLabel.material && (oldLabel.material as any).map) {
      (oldLabel.material as any).map.dispose();
    }
    if (oldLabel.material) {
      (oldLabel.material as THREE.Material).dispose();
    }
    labelMap.delete(key);
  }
  
  // 獲取 element 的實際位置
  const elementPosition = await getElementWorldPosition(modelId, localId);
  if (!elementPosition) {
    console.warn(`⚠️ Cannot get position for ${key}, skipping label creation`);
    return;
  }
  
  // 創建 canvas 來繪製文字
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d')!;
  const textString = text || 'Label';
  const textFontSize = fontSize || 16;
  const textColor = color || '#FFFFFF';
  
  // 設置 canvas 大小
  context.font = `bold ${textFontSize * 2}px Arial`;
  const metrics = context.measureText(textString);
  const textWidth = metrics.width;
  canvas.width = textWidth + 40;
  canvas.height = textFontSize * 2 + 20;
  
  // 繪製背景
  context.fillStyle = 'rgba(0, 0, 0, 0.8)';
  context.fillRect(0, 0, canvas.width, canvas.height);
  
  // 繪製邊框
  context.strokeStyle = textColor;
  context.lineWidth = 2;
  context.strokeRect(0, 0, canvas.width, canvas.height);
  
  // 繪製文字
  context.font = `bold ${textFontSize * 2}px Arial`;
  context.fillStyle = textColor;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(textString, canvas.width / 2, canvas.height / 2);
  
  // 創建 texture 和 plane mesh（使用 Mesh 而不是 Sprite，才能控制旋轉）
  const texture = new THREE.CanvasTexture(canvas);
  const planeGeometry = new THREE.PlaneGeometry(
    (canvas.width / canvas.height) * (textFontSize / 4),
    textFontSize / 4
  );
  const planeMaterial = new THREE.MeshBasicMaterial({ 
    map: texture,
    transparent: true,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false
  });
  const labelMesh = new THREE.Mesh(planeGeometry, planeMaterial);
  labelMesh.renderOrder = 999;
  
  // 使用自動 matrix 更新（讓 Three.js 處理）
  // Version: 2026-03-09-v2
  labelMesh.matrixAutoUpdate = true;
  
  // 設置位置（element 位置 + offset）- 與 cube 完全相同的邏輯
  const finalOffset = offset || { x: 0, y: 0, z: 0 };
  const localX = elementPosition.x + (finalOffset.x || 0);
  const localY = elementPosition.y + (finalOffset.y || 0) + 5;
  const localZ = elementPosition.z + (finalOffset.z || 0);
  
  // 設置旋轉（預設 rotateX -90 度，讓文字面向天空）
  const finalRotation = rotation || { x: -Math.PI / 2, y: 0, z: 0 };
  const rotX = finalRotation.x !== undefined ? finalRotation.x : -Math.PI / 2;
  const rotY = finalRotation.y || 0;
  const rotZ = finalRotation.z || 0;
  
  // 記錄 metadata
  (labelMesh as any).userData = { 
    modelId, 
    localId, 
    key, 
    text: textString, 
    color: textColor, 
    fontSize: textFontSize,
    rotation: { x: rotX, y: rotY, z: rotZ }
  };
  
  // ⚠️ 恢復正確的位置邏輯：加到 model.object（與 cube/beam 相同）
  labelMesh.matrixAutoUpdate = true;
  
  const labelModelInfo = modelManager?.getModelInfo(modelId);
  
  if (labelModelInfo && labelModelInfo.modelType === 'frag') {
    const labelFragmentModel = labelModelInfo.model as FRAGS.FragmentsModel;
    const labelModelObject = labelFragmentModel.object;
    
    // 設置位置：element 位置 + offset
    labelMesh.position.set(
      elementPosition.x + (finalOffset.x || 0),
      elementPosition.y + (finalOffset.y || 0) + 5,
      elementPosition.z + (finalOffset.z || 0)
    );
    
    // 將世界座標轉換為 model 的本地座標（與 cube/beam 完全相同）
    const worldPos = labelMesh.position.clone();
    const localPos = worldPos.sub(labelModelObject.position);
    labelMesh.position.copy(localPos);
    
    // 設置 rotation
    // ⚠️ 關鍵：由於 model.object 有 scale.y = -1（Y 軸翻轉），
    // 我們需要補償這個翻轉，讓 rotation 能正確顯示
    // 解決方案：反轉 X 和 Z 軸的 rotation
    labelMesh.rotation.set(-rotX, rotY, -rotZ);
    
    // 加到 model 的 object 下
    labelModelObject.add(labelMesh);
  } else {
    // 如果沒有找到 model，加到 scene
    labelMesh.position.set(localX, localY, localZ);
    labelMesh.rotation.set(rotX, rotY, rotZ);
    
    scene.add(labelMesh);
  }
  
  labelMap.set(key, labelMesh);
}

async function handleUpdateLabel(data: any) {
  const { modelId, localId, text, color, fontSize, offset, rotation } = data;
  const key = `${modelId}-${localId}`;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  const labelMesh = labelMap.get(key);
  if (!labelMesh) {
    console.warn(`⚠️ Label not found: ${key}, creating new one`);
    await handleCreateLabel(data);
    return;
  }
  
  // 如果需要更新文字、顏色或字體大小，重新創建 texture
  if (text !== undefined || color !== undefined || fontSize !== undefined) {
    const currentUserData = labelMesh.userData;
    const newText = text !== undefined ? text : currentUserData.text;
    const newColor = color !== undefined ? color : currentUserData.color;
    const newFontSize = fontSize !== undefined ? fontSize : currentUserData.fontSize;
    
    // 重新創建 canvas texture
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    
    context.font = `bold ${newFontSize * 2}px Arial`;
    const metrics = context.measureText(newText);
    const textWidth = metrics.width;
    canvas.width = textWidth + 40;
    canvas.height = newFontSize * 2 + 20;
    
    context.fillStyle = 'rgba(0, 0, 0, 0.8)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    context.strokeStyle = newColor;
    context.lineWidth = 2;
    context.strokeRect(0, 0, canvas.width, canvas.height);
    
    context.font = `bold ${newFontSize * 2}px Arial`;
    context.fillStyle = newColor;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(newText, canvas.width / 2, canvas.height / 2);
    
    // 更新 texture
    const newTexture = new THREE.CanvasTexture(canvas);
    if ((labelMesh.material as any).map) {
      (labelMesh.material as any).map.dispose();
    }
    (labelMesh.material as any).map = newTexture;
    (labelMesh.material as any).needsUpdate = true;
    
    // 更新 geometry 大小
    labelMesh.geometry.dispose();
    labelMesh.geometry = new THREE.PlaneGeometry(
      (canvas.width / canvas.height) * (newFontSize / 4),
      newFontSize / 4
    );
    
    // 更新 userData
    labelMesh.userData.text = newText;
    labelMesh.userData.color = newColor;
    labelMesh.userData.fontSize = newFontSize;
  }
  
  // 更新旋轉
  if (rotation !== undefined) {
    const currentRotation = labelMesh.userData.rotation || { x: -Math.PI / 2, y: 0, z: 0 };
    const newRotation = {
      x: rotation.x !== undefined ? rotation.x : currentRotation.x,
      y: rotation.y !== undefined ? rotation.y : currentRotation.y,
      z: rotation.z !== undefined ? rotation.z : currentRotation.z
    };
    
    // ⚠️ 關鍵：補償父物件的 Y 軸翻轉（scale.y = -1）
    // 反轉 X 和 Z 軸的 rotation
    labelMesh.rotation.set(-newRotation.x, newRotation.y, -newRotation.z);
    labelMesh.userData.rotation = newRotation;
  }
  
  // 更新位置
  if (offset !== undefined) {
    if (modelInfo.modelType !== 'frag') {
      console.warn(`⚠️ Model ${modelId} is not a fragment model`);
      return;
    }
    
    const fragmentModel = modelInfo.model as FRAGS.FragmentsModel;
    const updateModelObject = fragmentModel.object;
    
    const elementPosition = await getElementWorldPosition(modelId, localId);
    if (!elementPosition) {
      console.warn(`⚠️ Could not get element position for update`);
      return;
    }
    
    // 設置世界座標
    labelMesh.position.set(
      elementPosition.x + (offset.x || 0),
      elementPosition.y + (offset.y || 0) + 5,
      elementPosition.z + (offset.z || 0)
    );
    
    // 轉換為本地座標（與 cube/beam 相同）
    const worldPos = labelMesh.position.clone();
    const localPos = worldPos.sub(updateModelObject.position);
    labelMesh.position.copy(localPos);
  }
  
  // 觸發 Mapbox 重新渲染
  map.triggerRepaint();
}

function handleDeleteLabel(data: any) {
  const { modelId, localId } = data;
  const key = `${modelId}-${localId}`;
  
  // ⚠️ 關鍵：檢查當前頁面是否載入了這個 model
  const modelInfo = modelManager?.getModelInfo(modelId);
  if (!modelInfo) {
    return;
  }
  
  const labelMesh = labelMap.get(key);
  if (labelMesh) {
    // 移除 mesh
    if (labelMesh.parent) {
      labelMesh.parent.remove(labelMesh);
    }
    
    // 清理 geometry, texture 和 material
    if (labelMesh.geometry) {
      labelMesh.geometry.dispose();
    }
    if ((labelMesh.material as any).map) {
      (labelMesh.material as any).map.dispose();
    }
    if (labelMesh.material) {
      (labelMesh.material as THREE.Material).dispose();
    }
    
    labelMap.delete(key);
  }
}

// 清除所有舊的 cubes、beams 和 labels（頁面重新載入時）
function clearAllCubesAndBeams() {
  cubeMap.forEach((cube, key) => {
    // 從 parent 移除（可能是 scene 或 model.object）
    if (cube.parent) {
      cube.parent.remove(cube);
    }
    cube.geometry.dispose();
    (cube.material as THREE.Material).dispose();
  });
  cubeMap.clear();
  
  beamMap.forEach((beam, key) => {
    // 從 parent 移除（可能是 scene 或 model.object）
    if (beam.parent) {
      beam.parent.remove(beam);
    }
    beam.geometry.dispose();
    (beam.material as THREE.Material).dispose();
  });
  beamMap.clear();
  
  labelMap.forEach((labelMesh, key) => {
    // 移除 mesh
    if (labelMesh.parent) {
      labelMesh.parent.remove(labelMesh);
    }
    
    // 清理 geometry, texture 和 material
    if (labelMesh.geometry) {
      labelMesh.geometry.dispose();
    }
    if ((labelMesh.material as any).map) {
      (labelMesh.material as any).map.dispose();
    }
    if (labelMesh.material) {
      (labelMesh.material as THREE.Material).dispose();
    }
  });
  labelMap.clear();
}

// 頁面載入時清除舊的 cubes 和 beams
clearAllCubesAndBeams();

// 啟動 WebSocket 連接
connectWebSocket();

// 暴露清除函數到全域
(window as any).clearAllCubesAndBeams = clearAllCubesAndBeams;

// 給 share 頁設完 sessionStorage 後重新 identify 用（讓 share/iframe 收得到 WSS 推送）
// - 若 ws 已 OPEN：表示 ws.onopen 在 sessionStorage 設好前就 fire 了（用了 undefined），需重新 identify
// - 若 ws 還沒 OPEN：不用動，ws.onopen 自己會讀最新的 sessionStorage userId
(window as any).__echovReidentifyWS = () => {
  if (ws && ws.readyState === WebSocket.OPEN) {
    sendIdentify();
    setTimeout(() => sendRegisterModels(), 500);
  }
};

// 除錯函數：檢查場景中所有的 beams 和可疑物件
(window as any).inspectBeams = () => {
  console.log(`🔍 Total beams in beamMap: ${beamMap.size}`);
  beamMap.forEach((beam, key) => {
    console.log(`   Beam: ${key}`);
    console.log(`     Position: (${beam.position.x.toFixed(2)}, ${beam.position.y.toFixed(2)}, ${beam.position.z.toFixed(2)})`);
    console.log(`     Height: ${(beam.geometry as THREE.CylinderGeometry).parameters.height}`);
    console.log(`     Radius: ${(beam.geometry as THREE.CylinderGeometry).parameters.radiusTop}`);
    console.log(`     UserData:`, beam.userData);
  });
  
  // 檢查場景中所有 CylinderGeometry 的物件
  let sceneBeamCount = 0;
  scene.traverse((obj) => {
    if (obj instanceof THREE.Mesh && obj.geometry instanceof THREE.CylinderGeometry) {
      sceneBeamCount++;
      console.log(`   🔦 Found cylinder in scene:`, obj.userData);
      console.log(`     Position: (${obj.position.x.toFixed(2)}, ${obj.position.y.toFixed(2)}, ${obj.position.z.toFixed(2)})`);
      console.log(`     Parent:`, obj.parent?.type, obj.parent?.name);
    }
  });
  console.log(`🔍 Total cylinders in scene: ${sceneBeamCount}`);
  
  // 檢查所有高度很高的物件（可能是 beam）
  console.log(`\n🔍 Checking for tall objects (height > 1000)...`);
  let tallObjectCount = 0;
  scene.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      const bbox = new THREE.Box3().setFromObject(obj);
      const size = new THREE.Vector3();
      bbox.getSize(size);
      if (size.y > 1000) {
        tallObjectCount++;
        console.log(`   📏 Tall object found:`);
        console.log(`     Type: ${obj.geometry.type}`);
        console.log(`     Position: (${obj.position.x.toFixed(2)}, ${obj.position.y.toFixed(2)}, ${obj.position.z.toFixed(2)})`);
        console.log(`     Size: (${size.x.toFixed(2)}, ${size.y.toFixed(2)}, ${size.z.toFixed(2)})`);
        console.log(`     Material color:`, (obj.material as THREE.MeshBasicMaterial).color?.getHexString());
        console.log(`     UserData:`, obj.userData);
        console.log(`     Parent:`, obj.parent?.type);
      }
    }
  });
  console.log(`🔍 Total tall objects: ${tallObjectCount}`);
};

// 除錯函數：檢查 scene 結構
(window as any).inspectScene = () => {
  console.log(`🔍 Scene children count: ${scene.children.length}`);
  
  scene.children.forEach((child, index) => {
    console.log(`\n   Child ${index}: ${child.type} (${child.name || 'unnamed'})`);
    
    if (child instanceof THREE.Mesh) {
      console.log(`     Geometry: ${child.geometry.type}`);
      console.log(`     Position: (${child.position.x.toFixed(2)}, ${child.position.y.toFixed(2)}, ${child.position.z.toFixed(2)})`);
      console.log(`     UserData:`, child.userData);
      
      if (child.geometry instanceof THREE.CylinderGeometry) {
        console.log(`     ⚠️ THIS IS A CYLINDER (BEAM?)`);
      }
    }
    
    // 檢查是否有子物件
    if (child.children && child.children.length > 0) {
      console.log(`     Has ${child.children.length} children`);
      
      // 檢查子物件中是否有 cylinders
      let cylinderCount = 0;
      child.traverse((obj) => {
        if (obj instanceof THREE.Mesh && obj.geometry instanceof THREE.CylinderGeometry) {
          cylinderCount++;
        }
      });
      if (cylinderCount > 0) {
        console.log(`     ⚠️ Contains ${cylinderCount} cylinders!`);
      }
    }
  });
};

// 除錯函數：檢查所有 models 的位置
(window as any).inspectModelPositions = () => {
  const allModels = (window as any).modelManager.getAllModels();
  console.log(`🔍 Checking positions of all ${allModels.size} models...`);
  
  allModels.forEach((modelInfo: any, modelId: string) => {
    if (modelInfo.modelType === 'frag') {
      const model = modelInfo.model;
      const pos = model.object.position;
      console.log(`   Model: ${modelId}`);
      console.log(`     Position: (${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)})`);
      console.log(`     Visible: ${modelInfo.visible}`);
    }
  });
};

(window as any).inspectLabels = () => {
  console.log(`🔍 Total labels in labelMap: ${labelMap.size}`);
  labelMap.forEach((labelMesh, key) => {
    console.log(`   Label: ${key}`);
    console.log(`     Text: "${labelMesh.userData.text}"`);
    console.log(`     matrixAutoUpdate: ${labelMesh.matrixAutoUpdate}`);
    
    // 從 matrix 提取 position
    const posFromMatrix = new THREE.Vector3();
    posFromMatrix.setFromMatrixPosition(labelMesh.matrix);
    console.log(`     Position (from matrix): (${posFromMatrix.x.toFixed(2)}, ${posFromMatrix.y.toFixed(2)}, ${posFromMatrix.z.toFixed(2)})`);
    
    // 從 matrix 提取 rotation
    const rotFromMatrix = new THREE.Euler();
    rotFromMatrix.setFromRotationMatrix(labelMesh.matrix);
    console.log(`     Rotation (from matrix): (${rotFromMatrix.x.toFixed(2)}, ${rotFromMatrix.y.toFixed(2)}, ${rotFromMatrix.z.toFixed(2)})`);
    console.log(`     Rotation (userData): (${labelMesh.userData.rotation?.x?.toFixed(2)}, ${labelMesh.userData.rotation?.y?.toFixed(2)}, ${labelMesh.userData.rotation?.z?.toFixed(2)})`);
    
    const worldPos = new THREE.Vector3();
    labelMesh.getWorldPosition(worldPos);
    console.log(`     Position (world): (${worldPos.x.toFixed(2)}, ${worldPos.y.toFixed(2)}, ${worldPos.z.toFixed(2)})`);
    console.log(`     Parent type: ${labelMesh.parent?.type || 'none'}`);
    console.log(`     Parent name: ${(labelMesh.parent as any)?.name || 'none'}`);
    console.log(`     Parent uuid: ${labelMesh.parent?.uuid || 'none'}`);
  });
};

// ========================================
// 暴露到全域（方便除錯）
// 注意：modelManager 已經在初始化時暴露了
const authClient = new AuthClient();
(window as any).authClient = authClient;
(window as any).lodCamera = lodCamera;
(window as any).map = map;
(window as any).ANIMATION_CONFIG = ANIMATION_CONFIG; // 暴露動畫配置以便調整
(window as any).MOBILE_PERFORMANCE_CONFIG = MOBILE_PERFORMANCE_CONFIG; // 暴露手機效能配置
(window as any).isMobileDevice = isMobileDevice; // 暴露裝置檢測結果
(window as any).scene = scene;
(window as any).camera = camera;
(window as any).THREE = THREE;
(window as any).captureSceneState = captureSceneState;
(window as any).createShareLink = createShareLink;
(window as any).loadAndDisplayMarkers = loadAndDisplayMarkers;
(window as any).cubeMap = cubeMap;
(window as any).beamMap = beamMap;
(window as any).labelMap = labelMap;

// ========================================
